/**
 * English screen copy for the internal operations portal.
 *
 * Internal Ops ships only English interface copy. French product content is
 * edited as catalog data and remains available to the other portals.
 *
 * Only screen copy lives here. Strings that must be identical on every surface -
 * the guest stage model, notification templates, receipts - stay in the platform
 * catalogue in `@croissants/i18n`.
 */

export const en = {
  // The order page's Stripe group (components/stripe.tsx): the payment id opens its own page.
  'stripe.group': 'Stripe',
  'stripe.payment': 'Payment',
  'stripe.card': 'Card',
  'stripe.captured': 'Captured',
  'stripe.fee': 'Stripe fee',
  'support.column.status': 'Status',
  // -- shell ----------------------------------------------------------------
  'app.title': 'Croissant in Bed operations',
  'app.shortTitle': 'Operations',
  'app.skipToContent': 'Skip to content',
  /* The shell's own controls (packages/ui `AppShell`). They are accessible names
     only - the glyphs carry no text - and each pair changes with the state it
     describes, so a screen reader hears what the control will DO next. */
  'app.openMenu': 'Open menu',
  'app.closeMenu': 'Close menu',
  'app.collapseSidebar': 'Collapse sidebar',
  'app.expandSidebar': 'Expand sidebar',
  'app.signOut': 'Sign out',
  'app.signedInAs': 'Signed in as {name}',
  // The account menu, top right, identical in both staff portals.
  'app.account': 'Your account',
  'app.accountSettings': 'Account settings',
  'app.signedInHint': 'Signed in',
  'app.accountUnknown': 'Signed-in user',
  // Table chrome, shared with the partner portal. `{total}` is a STRING: a
  // cursor page that hit its limit knows only that there is more, and shows "200+".
  'table.pagination': 'Pages',
  'table.range': '{from}-{to} of {total}',
  'table.first': 'First page',
  'table.previous': 'Previous page',
  'table.next': 'Next page',
  'table.last': 'Last page',
  'table.rows': { one: '{count} row', other: '{count} rows' },
  'toast.region': 'Notifications',

  'nav.dashboard': 'Dashboard',
  'nav.activity': 'Activity',
  /* The two halves of what was one "Organizations" destination (PRD 5.6). */
  'nav.bakeries': 'Bakeries',
  'nav.properties': 'Clients',
  'catalogue.title': 'Menus',
  'catalogue.pool': 'Shelf',
  'catalogue.poolPlatform': 'Platform (mode A)',
  'catalogue.poolShop': '{name} ({count} own items)',
  'catalogue.empty': 'Nothing on this shelf yet.',
  'catalogue.add': 'Add an item',
  'catalogue.edit': 'Edit',
  'catalogue.save': 'Save',
  'catalogue.saving': 'Saving',
  'catalogue.cancel': 'Cancel',
  'catalogue.moveUp': 'Move {name} earlier',
  'catalogue.moveDown': 'Move {name} later',
  'catalogue.retired': 'Retired',
  'catalogue.noFrench': 'No French copy',
  'catalogue.kind': 'Kind',
  'catalogue.price': 'Sale price',
  'catalogue.vat': 'VAT',
  'catalogue.availability.IN_STOCK': 'On sale',
  'catalogue.availability.SOLD_OUT_TODAY': 'Sold out today',
  'catalogue.availability.HIDDEN': 'Sold out until further notice',
  'catalogue.copyEn': 'English copy',
  'catalogue.copyFr': 'French copy',
  'catalogue.frHint': 'Blank falls back to English.',
  'catalogue.frFallback': '(blank falls back to English)',
  'catalogue.name': 'Name',
  'catalogue.description': 'Description',
  'catalogue.photos': 'Photos',
  'catalogue.addPhoto': 'Add a photo',
  'catalogue.removePhoto': 'Remove',
  'catalogue.uploading': 'Uploading\u2026',
  'catalogue.uploadFailed': 'That image could not be stored. Try a smaller file.',
  'catalogue.alt': 'Alt text',
  'catalogue.altHint': 'For guests who cannot see the photo.',
  'catalogue.ingredients': 'Ingredients',
  'catalogue.ingredientsHint': 'One per line.',
  'catalogue.maxQuantity': 'Max per order',
  /* A section of the guest menu, named here (CAT-016). */
  'catalogue.addType': 'Add a type',
  'catalogue.addTypeTitle': 'New type',
  'catalogue.addTypeField': 'Name',
  'catalogue.addTypeSave': 'Add',
  'catalogue.close': 'Close',
  'catalogue.zoomPhoto': 'Open the photo of {name}',
  'catalogue.zoomPhotoPlain': 'Open the photo',
  'catalogue.previousPhoto': 'Previous photo',
  'catalogue.nextPhoto': 'Next photo',
  'catalogue.photoPosition': '{position} of {count}',
  'catalogue.allergens': 'Allergens',
  'catalogue.dietary': 'Dietary tags',
  /*
   * The guest-visibility control. It writes `listed`, not `active`: a product
   * can be real, priced and paid for and still be sold only inside a formule
   * (docs/plans/mode-a-menu-and-pricing.html section 8). The words are the
   * founder's - hiding one was "unclicking on the menu".
   */
  'catalogue.listed': 'On the menu',
  /*
   * The Menus tab's own columns (docs/plans/menus-tab-mockup.html). Every price
   * is set on the row, so the tab needs headings the product form never did.
   */
  'catalogue.column.product': 'Product',
  /* Names what the picker in the Baker price header CHANGES, for a reader who
     cannot see that it sits over one column. */
  'catalogue.column.house': 'Prices shown for',
  /* Over the switch beside it: does this house supply this product at all? It
     is a heading and not a caption - a switch nobody can name is worse than a
     word over the column. */
  'catalogue.column.offered': 'Offered',
  /*
   * EVERY FIGURE IN THESE THREE COLUMNS IS VAT-INCLUSIVE, and each heading says
   * so (founder, 2026-08-28). The baker price is what a house keeps, TTC; the
   * guest price is what a guest pays, TTC; and the margin is the gap between
   * them, so it is TTC by arithmetic and can be nothing else. A column of euro
   * amounts that does not state which side of the tax it sits on is one an
   * operator has to ask about, and the answer differs by product - the food
   * rate is 5,5 % and a quiche is 10 %.
   */
  'catalogue.column.bakerPrice': 'Baker price TTC',
  'catalogue.column.guestPrice': 'Guest price TTC',
  'catalogue.column.margin': 'Margin TTC',
  'catalogue.column.order': 'Running order',
  'catalogue.kindOf': 'Kind of {name}',
  'catalogue.listedFor': '{name} on the menu',
  'catalogue.offeredFor': '{name} offered by this bakery',
  /* Under a formule's empty box: what its parts come to for this house. */
  'catalogue.componentsSum': 'sum {amount}',
  /* Under an empty box on a row that is SOLD - on the menu, or a choice inside
     a formule - and so owes this house a price. Three words, in the danger
     tone, on a row painted the same: the table is read by scanning it. */
  'catalogue.noBakerPrice': 'No baker price',
  /* Under a locked box on a sold row this house does not offer: a state, not a gap. */
  'catalogue.notOffered': 'Not offered',
  'catalogue.group.count': { one: '{count} item', other: '{count} items' },
  'catalogue.group.countNoneListed': {
    one: '{count} item, not on the menu',
    other: '{count} items, none on the menu',
  },
  // The sections a menu is divided into, and the words over each (CAT-016).
  'kinds.title': 'Kinds',
  'kinds.add': 'Add a kind',
  'kinds.delete': 'Delete',
  'kinds.empty': 'No kinds yet.',
  'kinds.column.en': 'English',
  'kinds.column.fr': 'French',
  'kinds.column.items': 'Products',
  'kinds.column.order': 'Running order',
  'kinds.moveUp': 'Move {name} earlier',
  'kinds.moveDown': 'Move {name} later',
  'kinds.dialog.add': 'New kind',
  'kinds.delete.title': 'Delete {name}?',
  'kinds.inUse': { one: '{count} product uses it', other: '{count} products use it' },
  /* `catalogue_items.public_id`: the code an invoice line names (section 9). */
  'catalogue.productCode': 'Product code',
  'catalogue.allergen.GLUTEN': 'Gluten',
  'catalogue.allergen.CRUSTACEANS': 'Crustaceans',
  'catalogue.allergen.EGGS': 'Eggs',
  'catalogue.allergen.FISH': 'Fish',
  'catalogue.allergen.PEANUTS': 'Peanuts',
  'catalogue.allergen.SOYBEANS': 'Soybeans',
  'catalogue.allergen.MILK': 'Milk',
  'catalogue.allergen.NUTS': 'Nuts',
  'catalogue.allergen.CELERY': 'Celery',
  'catalogue.allergen.MUSTARD': 'Mustard',
  'catalogue.allergen.SESAME': 'Sesame',
  'catalogue.allergen.SULPHITES': 'Sulfites',
  'catalogue.allergen.LUPIN': 'Lupin',
  'catalogue.allergen.MOLLUSCS': 'Molluscs',
  'catalogue.dietary.VEGETARIAN': 'Vegetarian',
  'catalogue.dietary.VEGAN': 'Vegan',
  'catalogue.dietary.GLUTEN_FREE': 'Gluten free',
  'catalogue.dietary.LACTOSE_FREE': 'Lactose free',
  'catalogue.dietary.HALAL': 'Halal',

  /*
   * A FORMULE's contents, on the menu screen
   * (docs/plans/mode-a-menu-and-pricing.html section 7). One "line" is one
   * bullet of the printed card: a quantity, its own wording, and the products
   * that satisfy it - one is a fixed ingredient, several are a choice the guest
   * makes.
   */
  'formule.title': 'Composition',
  'catalogue.required': 'required',
  'catalogue.product.tabs': 'Product',
  'catalogue.product.tab.description': 'Description',
  'catalogue.product.tab.visuals': 'Visuals',
  'catalogue.product.tab.allergens': 'Allergens and dietary info',
  'catalogue.product.tab.formule': 'Formule',
  'catalogue.product.gone': 'This product is no longer on this shelf.',
  'action.moveUp': 'Move up',
  'action.moveDown': 'Move down',
  'formule.isFormule': 'Is this a formule?',
  'formule.isFormuleLabel': 'This product is a formule',
  'formule.bundledLabel': 'Any part short cancels the whole formule',
  'formule.followsPartsLabel': 'Price follows its parts',
  'formule.sectionTitle': 'Section title',
  'formule.sectionTitleFr': 'Section title (French)',
  'formule.sectionTitlePlaceholder': 'Which pastry, which drink...',
  'formule.sectionUntitled': 'Untitled section',
  'formule.sectionEmpty': 'No products in this section yet.',
  'formule.sectionActions': 'Actions for {name}',
  // What a section IS, as plain text beside its heading - not a state, so not a pill (#2004).
  'formule.section.INCLUDED': 'Always included',
  'formule.section.GUEST_CHOOSES': 'Guest chooses',
  'formule.itemActions': 'Actions for {name}',
  'formule.itemCount': 'How many {name}',
  'formule.itemCountMore': 'One more {name}',
  'formule.itemCountFewer': 'One fewer {name}',
  'formule.supplement': 'Additional cost',
  'formule.supplementFor': 'Additional cost for {name}',
  'formule.picks': 'Number of items the guest needs to select',
  'formule.picksMore': 'One more to select',
  'formule.picksFewer': 'One fewer to select',
  'formule.empty': 'No lines yet.',
  'formule.addIncludedSection': 'Add an included section',
  'formule.addChoiceSection': 'Add a choice section',
  'formule.removeBullet': 'Remove',
  'formule.quantity': 'Quantity',
  'formule.note': 'Note',
  'formule.addChoice': 'Add products',
  'formule.removeChoice': 'Remove {name}',
  'formule.pickSearch': 'Search',
  'formule.pickEmpty': 'No product matches.',
  'formule.componentsGuestPrice': 'Components',
  'formule.guestPays': 'Guest pays',
  'formule.gap': 'Gap',
  'formule.houses': 'What each house keeps',

  /*
   * One house's side of the mode A deal, on the formule editor's per-house
   * table. The commission is the gap between the guest price and what the house
   * keeps, and it is derived server-side, never here.
   */
  'prices.column.house': 'House',
  'prices.column.bakeryPrice': 'House keeps',
  'prices.column.commissionTtc': 'Commission incl. VAT',
  'prices.column.commissionNet': 'Commission excl. VAT',

  'nav.devices': 'Devices',
  'nav.support': 'Support',
  'nav.finance': 'Finance',
  /* The administered platform settings (OPS-025). After Finance because it is
     read rarely and changes what every other screen is looking at. */
  'nav.settings': 'Settings',
  'nav.quality': 'Quality and ratings',
  'nav.map': 'Coverage',
  'nav.pilot': 'Pilot',
  /* One entry owning Health status, Dead letter, Kill switch and Audit log.
     Those four keep their own titles - the sub-tabs are labelled from
     `health.title` and friends, so a strip cannot drift from the heading under
     it. See screens/Debug.tsx. */
  'nav.debug': 'Debug',

  // -- auth -----------------------------------------------------------------
  'auth.signIn.loading': 'Preparing sign-in',
  'auth.dev.title': 'Development sign-in',
  'auth.dev.warning':
    'This development build is signing in through the API development header bypass rather than through Clerk. The API refuses these headers in production. Append ?devAuth=0 to the URL to go back to Clerk sign-in.',
  'auth.dev.role': 'Role',
  'auth.dev.role.hint': 'Sent as the X-Dev-Role header on every request.',
  'auth.dev.org': 'Organization id',
  'auth.dev.org.hint': 'Optional. Sent as X-Dev-Org when set.',
  'auth.dev.submit': 'Enter the portal',
  'auth.unconfigured.title': 'Configuration missing',
  'auth.unconfigured.body':
    'VITE_CLERK_INTERNAL_PUBLISHABLE_KEY is not set on this build, and the development bypass is only offered on a development build. Set the internal publishable key and redeploy.',
  'auth.apiMissing.title': 'API address missing',
  'auth.apiMissing.body':
    'VITE_API_BASE_URL is not set. No host is hardcoded in this portal, so nothing can be loaded until it is configured.',

  // -- pilot (screens/Pilot.tsx) --------------------------------------------
  'pilot.title': 'Pilot',
  'pilot.portals': 'Portals',
  'pilot.link.owner': 'Property owner portal',
  /* The same portal, opened on the demo BAKERY organization (BAK-028) - the
     owner portal serves bakeries too, and the link carries which one. */
  'pilot.link.bakerOrg': 'Baker organization portal',
  'pilot.link.guestA': 'Guest, mode A',
  'pilot.link.guestB': 'Guest, mode B',
  /* The two entries that choose what they open. Each names the KIND of thing
     it picks, not the entry it sits in: the card's own title already says which
     portal this is, and a select labelled "Guest, mode A" inside a section
     titled "Guest, mode A" says it twice. Never rendered - it is the accessible
     name of a control whose current value is the visible one. */
  'pilot.pick.apartment': 'Apartment',
  /* One cookie opens every portal, so ending it ends them all - the label says
     so rather than leaving an operator to discover it on the other tabs. */
  'pilot.end': 'End session on all portals',
  'pilot.closed': 'No pilot session',
  'pilot.open': 'Start a session',

  // -- generic states -------------------------------------------------------
  'state.loading': 'Loading',
  'state.empty': 'Nothing to show',
  'state.error.title': 'That request failed',
  'state.error.retry': 'Try again',
  'state.error.requestId': 'Request id {id}',
  'state.saved': 'Saved',
  'state.saving': 'Saving',

  'action.apply': 'Apply',
  'action.cancel': 'Cancel',
  'action.close': 'Close',
  'action.confirm': 'Confirm',
  'action.refresh': 'Refresh',
  'action.preview': 'Preview',
  'action.search': 'Search',
  'action.filter': 'Filter',
  'action.approve': 'Approve',
  'action.clear': 'Clear',
  'action.about': 'What this screen is for',
  'action.back': 'Back',

  'field.serviceDay': 'Service day',
  'field.from': 'From',
  'field.to': 'To',
  'field.required': '(required)',
  'field.all': 'All',
  'filter.search': 'Search',
  'filter.selectAll': 'Select all',
  'filter.clearAll': 'Clear all',
  'field.limit': 'Rows',

  'confirm.typeToConfirm': 'Type {word} to confirm',
  'confirm.mismatch': 'That does not match.',
  'confirm.word': 'CONFIRM',
  'danger.title': 'Danger zone',

  // -- admin dashboard (OPS-001) ----------------------------------------------
  'dashboard.title': 'Admin dashboard',
  'dashboard.subtitle': 'Every order for the service day, sorted by lateness risk.',
  'dashboard.live': 'Live, refreshed every {seconds}s',
  'dashboard.paused': 'Auto-refresh paused',
  'dashboard.pause': 'Pause',
  'dashboard.resume': 'Resume',
  'dashboard.updatedAt': 'Updated {time}',
  'dashboard.serverTime': 'Server time {time}',
  'dashboard.count': { one: '{count} order', other: '{count} orders' },
  'dashboard.flaggedOnly': 'Flagged only',
  'dashboard.filter.status': 'Status',
  'dashboard.filter.bakery': 'Bakery',
  'dashboard.column.window': 'Window',
  'dashboard.column.order': 'Order',
  'dashboard.column.property': 'Property',
  'dashboard.column.bakery': 'Bakery',
  'dashboard.column.status': 'Status',
  'dashboard.column.delivery': 'Delivery',
  'dashboard.column.courier': 'Courier',
  'dashboard.column.risk': 'Risk',
  'dashboard.column.incidents': 'Incidents',
  'dashboard.noBakery': 'Not allocated',
  'dashboard.noCourier': 'No courier yet',
  'dashboard.noDelivery': 'No provider job',
  'dashboard.mode': 'Mode {mode}',
  'dashboard.openIncidents': { one: '{count} incident', other: '{count} incidents' },
  'dashboard.eta': 'ETA {time}',
  'dashboard.etaUnknown': 'No ETA',

  'severity.lateBy': '{delta} late',
  'severity.slack': '{delta} spare',
  'severity.onTime': 'Within the window',

  'tally.late': 'Late',
  'tally.atRisk': 'At risk',
  'tally.watch': 'Watch',
  'tally.onTrack': 'On track',
  'tally.closed': 'Closed',
  'tally.incidents': 'Open incidents',

  // -- order detail ---------------------------------------------------------
  'order.title': 'Order {code}',
  'order.tabs.summary': 'Order summary',
  'order.tabs.financials': 'Order financials',
  'order.tabs.support': 'Support',
  'order.tabs.activity': 'Activities',
  'order.tabs.invoices': 'Invoices',
  'order.back': 'Back to the dashboard',
  /* The one line on the screen that says what an operator must DO, at the top
     of the page and only once the order has ENDED with the guest's money
     still held and no refund decided (`guestPaidAndGotNothing`, #1758). The
     amount is what is still owed: the total paid, less anything already
     returned. */
  'order.owed.paidNothingReceived':
    'The guest paid and received nothing. {amount} is still owed back to them.',
  /* RTE-035, #1758: a courier search in progress - since when, and the
     instant the platform gives up and cancels. Both 24-hour clock times. */
  'order.courierSearch': 'Finding a courier since {since}. Gives up at {deadline}.',
  /* GitHub #1299: the courier took a bag nobody at the counter declared ready.
     Drawn while `pickupUndeclared` holds, and it clears itself - the counter's
     late Ready tap, its handed-over tap, or the bag arriving. The instant is
     the pickup's own, read off the order's event log. */
  'order.pickupUndeclared':
    'Collected by the courier at {at}; the counter had not marked the bag ready.',
  'order.summary.eta': 'Estimated arrival',
  /* The same line once the delivery is over: the instant it actually arrived. */
  'order.summary.arrived': 'Arrived',
  'order.summary.pickupEta': 'Courier at the counter',
  // The courier's two booked windows (OPS-001, RTE-032), each just above the step it governs.
  'order.summary.collectBooked': 'Pickup booked',
  'order.summary.deliverBooked': 'Delivery booked',
  'order.summary.promised': 'Promised at checkout',
  'order.summary.provider': 'Provider',
  // Stuart's own words for its two ids, so an operator can search its dashboard.
  'order.summary.jobId': 'Job ID',
  'order.summary.packageId': 'Package ID',
  'order.summary.vehicle': 'Vehicle',
  'order.summary.providerRefund': 'Provider refund',
  'order.summary.providerRefundValue': 'Credited {credit}, platform liable {liable} ({reasons})',
  'order.summary.answerableCounter': 'Refusal came from',
  'order.summary.answerableCounterUnnamed': 'A counter that no longer exists',
  'order.summary.guest': 'Guest',
  'order.summary.mobile': 'Mobile',
  'order.summary.email': 'Email',
  'order.summary.note': 'Order note',
  'order.summary.whatsapp': 'WhatsApp',
  'order.summary.call': 'Call',
  'order.summary.sms': 'SMS',
  'order.summary.address': 'Address',
  'order.summary.apartment': 'Apartment',
  // The four groups of the order record's first card.
  'order.group.order': 'Order',
  'order.group.guest': 'Guest',
  'order.group.delivery': 'Delivery',
  'order.group.timing': 'Timing',
  /* What the guest paid, under the line where the money moved (founder,
     2026-09-07). `PAYMENT_CAPTURED` carries no payload, so this is the whole
     of what that row can say. */
  'order.payment.total': 'Paid by the guest',
  'order.payment.mode': 'Order type',
  'order.payment.mode.IMMEDIATE': 'Immediate',
  'order.payment.mode.PREORDER': 'Pre-order',
  'order.payment.merchandise': 'Products',
  'order.payment.deliveryFee': 'Delivery charged',
  'order.payment.serviceFee': 'Service fee charged',
  'order.payment.itemCount': 'Items',
  'order.payment.stay': 'Stay',
  'order.payment.stayMornings': '{count} mornings',
  'order.summary.refundedTotal': 'Refunded',
  'order.summary.substitutionAbsorbed': 'Substitution absorbed',
  'order.section.financials': 'Order financials',
  'order.section.bagPhotos': 'Bag photos',
  'order.bagPhotos.skipped': 'Skipped at the counter',
  'order.bagPhotos.cameraUnavailable': 'Camera unavailable',
  'order.bagPhotos.uploading': 'Photos uploading',
  'order.bagPhotos.photoOf': 'Photo {index} of {count} - {time}',
  'order.bagPhotos.previous': 'Previous',
  'order.bagPhotos.next': 'Next',
  'order.section.events': 'Order events',
  'order.actions': 'Actions',
  /* The liability review and the provider claim (PAY-013, PAY-030). */
  /* The panel is the REFUND REVIEW (register decision 10): one state for every
     refund waiting on a person, keeping the four answers. The wire field is
     still called `liability` and these keys still are - renaming a response
     field in the same deploy as the state rename is two breaking renames in
     one window, and nobody has asked for it. */
  'order.section.liability': 'Refund review',
  'order.claim.state': 'Provider claim',
  'order.claim.move': 'Move the claim',
  'order.claim.reason': 'Reason',
  /* A payload field on order events a previous release wrote (the liability
     review's answer); `order_events` is append-only, so the words stay. */
  'decision.REFUND': 'Refund in full',
  'decision.PARTIAL_REFUND': 'Refund part of it',
  'decision.NOT_REFUNDED': 'Not refunded',
  'decision.CLAIM': 'Courier liable: refund and claim',
  'claim.NONE': 'None',
  'claim.OPEN': 'Open',
  'claim.SETTLED': 'Settled',
  'claim.REJECTED': 'Rejected',
  'claim.ESCALATED': 'Escalated',
  'claim.WRITTEN_OFF': 'Written off',
  'claimAction.SETTLE': 'Provider paid',
  'claimAction.REJECT': 'Provider refused',
  'claimAction.ESCALATE': 'Escalate',
  'claimAction.WRITE_OFF': 'Write it off',
  'order.allocation.none': 'No breakdown on this order yet.',
  // Since when these rates have applied, and where to read them. No version
  // number: it is the highest of three rate versions and names no row anyone
  // can go and look at (see the comment in OrderDetail.tsx).
  'order.financials.ratesInForce': 'Rates in force since {at}.',
  'order.financials.ratesLink': 'See commercial rates',
  'order.financials.section.guest': 'Guest',
  'order.financials.section.baker': 'Baker',
  'order.financials.section.owner': 'Owner',
  'order.financials.section.croissant': 'Croissant',
  // The revenue share beside the tables (components/RevenueShare.tsx).
  'order.share.title': 'Revenue share',
  'order.share.baker': 'Baker excl. VAT',
  'order.share.bakerVat': 'Baker VAT',
  'order.share.croissant': 'Croissant excl. VAT',
  'order.share.croissantVat': 'Croissant VAT',
  'order.share.owner': 'Owner',
  'order.share.delivery': 'Delivery incl. tip',
  'order.share.total': 'Guest paid incl. tip',
  'order.financials.column.item': 'Item',
  'order.financials.column.quantity': 'Qty',
  'order.financials.column.vatRate': 'VAT rate',
  'order.financials.column.base': 'Base',
  'order.financials.column.rate': 'Rate',
  'order.financials.column.guestPrice': 'Guest price',
  'order.financials.column.bakerPrice': 'Baker price',
  'order.financials.column.excludingVat': 'Excl. VAT',
  'order.financials.column.vat': 'VAT',
  'order.financials.column.includingVat': 'Incl. VAT',
  'order.financials.kind.PRODUCT': 'Product',
  'order.financials.kind.PRODUCTS_SUBTOTAL': 'Products subtotal',
  'order.financials.kind.DELIVERY': 'Delivery fee',
  'order.financials.kind.SERVICE': 'Service fee',
  'order.financials.kind.SMALL_ORDER': 'Small order fee',
  'order.financials.kind.FEE_DISCOUNT': 'Discount',
  'order.financials.kind.COMMISSION': 'Croissant commission',
  'order.financials.kind.OWNER_COMMISSION': 'Owner commission',
  'order.financials.kind.DELIVERY_COST': 'Delivery paid by Croissant',
  // How a figure was reached, on hover.
  'order.financials.hint.units': '{quantity} \u00d7 {unit}',
  // A set's product: the set's price shared by the products' shelf prices.
  'order.financials.hint.share': '{whole} \u00d7 {weight} / {weights} (shelf prices)',
  'order.financials.hint.gap': 'Guest price {guest} - baker price {baker}',
  'order.financials.hint.rate': '{base} \u00d7 {rate}',
  'order.financials.hint.vat': '{rate} \u00d7 {amount}',
  'order.financials.hint.deliveryFinal': 'Final cost {cost} - paid by the guest {paid}',
  'order.financials.hint.deliveryQuoted': 'Quoted cost {cost} - paid by the guest {paid}',
  'order.financials.gross': '{amount} incl. VAT',
  'order.financials.total': 'Total',
  // A decided Support case after a table's total; its reasons go on the line below.
  'order.financials.refund': 'Refund',
  'order.financials.totalAfterRefund': 'Total after refund',
  'order.events.empty': 'No events recorded yet.',
  'order.events.actor': 'by {actor}',
  'order.events.bookingRequested': 'Courier booking requested',
  /* Who a cancellation told, one line per party (#2125, decision
     cancellation-who-was-told): "Guest: not told, never charged". */
  'order.audience.line': '{party}: {reason}',
  'order.audience.party.GUEST': 'Guest',
  'order.audience.party.COUNTER': 'Counter',
  'order.audience.party.PROVIDER': 'Delivery company',
  'order.audience.party.PARTNER': 'Conciergerie or owner',
  'order.audience.party.OPERATIONS': 'Operations',
  'order.audience.reason.GUEST_MESSAGE_CANCELLED': 'told, order canceled',
  'order.audience.reason.GUEST_MESSAGE_UNFULFILLED': 'told, no bakery could take it',
  'order.audience.reason.GUEST_MESSAGE_PREPARATION_ISSUE': 'told, it could not be prepared',
  'order.audience.reason.NEVER_PAID': 'not told, never charged',
  'order.audience.reason.GUEST_TOLD_BY_INGEST':
    'nothing to tell, the delivery-failed message says it',
  'order.audience.reason.COUNTER_CANCELLED_TILE': 'told, on its board',
  'order.audience.reason.NEVER_OFFERED': 'nothing to tell, never offered',
  'order.audience.reason.NEVER_ACCEPTED': 'nothing to tell, never accepted',
  'order.audience.reason.BAG_COLLECTED': 'nothing to tell, the courier had the bag',
  'order.audience.reason.COUNTER_OWN_DECISION': 'nothing to tell, its own decision',
  'order.audience.reason.COURIER_BOOKING_CANCELLED': 'told, courier booking canceled',
  'order.audience.reason.COURIER_CANCEL_REFUSED': 'told, refused the cancel, asked again',
  'order.audience.reason.NO_COURIER_BOOKED': 'nothing to tell, no courier booked',
  'order.audience.reason.PROVIDER_REPORTED_FAILURE': 'nothing to tell, it reported the failure',
  'order.audience.reason.BY_DECISION': 'not told, by decision',
  'order.audience.reason.ALERT_UNALLOCATED': 'alerted, no bakery could take it',
  'order.audience.reason.ALERT_REVALIDATION_CANCELLED': 'alerted, canceled by the nightly check',
  'order.audience.reason.ALERT_PROVIDER_CANCEL_FAILED':
    'alerted, the delivery company refused the cancel',
  'order.audience.reason.ALERT_DELIVERY_FAILED': 'alerted, the delivery failed',
  'order.audience.reason.NO_ALERT_NEEDED': 'no alert needed',
  /*
   * The order's history as a DEBUG LOG (founder, 2026-09-17): a timing table
   * above the rows - each milestone against the deadline that governed it - and
   * every row tagged with what it is. The tags are single words (pills are;
   * `components/Badge`), the milestones and columns are ordinary text.
   */
  'order.timing.milestone': 'Milestone',
  'order.timing.due': 'Due',
  'order.timing.actual': 'Actual',
  'order.timing.verdict': 'On time?',
  'order.timing.PAID': 'Paid',
  'order.timing.OFFERED': 'Offered to the bakery',
  'order.timing.ACCEPTED': 'Accepted by the bakery',
  'order.timing.COURIER_BOOKED': 'Courier booked',
  'order.timing.BAG_READY': 'Bag ready',
  'order.timing.COURIER_AT_COUNTER': 'Courier at the counter',
  'order.timing.PICKED_UP': 'Picked up',
  'order.timing.DELIVERED': 'Delivered',
  'order.timing.spare': '{delta} spare',
  'order.timing.over': '{delta} late',
  'order.timing.early': '{delta} early',
  'order.timing.courierTold': 'Courier told',
  'order.timing.collect': 'collect {span}',
  'order.timing.deliver': 'deliver {span}',
  'order.timing.paidToDelivered': 'Paid to delivered',
  'order.timing.guestWindow': 'Guest window',
  'order.exchange.processed': 'processed',
  'order.exchange.processingFailed': 'processing failed: {error}',
  'order.exchange.filed': 'filed: {reason}',
  'order.exchange.unprocessed': 'not processed',
  // -- the SupportConversation (SUP-006, GST-039) ----------------------------
  'order.section.support': 'Support conversation',
  'order.support.subtitle':
    'Every message to and from the guest. A reply goes by SMS, and by email when they gave one.',
  'order.support.empty': 'No support message on this order.',
  'order.support.fromGuest': 'Guest',
  'order.support.fromOps': 'Operations',
  'order.support.toGuest': 'To the guest',
  'order.support.status.FAILED': 'Failed',
  'order.support.status.HELD': 'Held',
  'order.support.toSupport': 'To support',
  'order.support.deliveryFailed': 'Delivery failed',
  'order.support.scheduled': 'Scheduled {time}',
  'order.support.retrying': 'Retrying {time}',
  'order.support.photo': 'Guest photo',
  'order.support.receipt.SENT': 'Sent',
  'order.support.receipt.DELIVERED': 'Delivered',
  'order.support.receipt.READ': 'Read',
  'order.support.receipt.FAILED': 'Not delivered',
  'order.support.receipt.BOUNCED': 'Bounced',
  'order.support.receipt.DELAYED': 'Delayed',
  'order.support.receipt.COMPLAINED': 'Marked as spam',
  // -- everything else attached to the order (founder, 2026-10-26) ------------
  'order.section.cases': 'Cases',
  'order.cases.status.OPEN': 'Open',
  'order.cases.status.DECIDED': 'Decided',
  'order.cases.decided': 'Decided by {by}, {at}',
  'order.cases.bank.WON': 'Dispute won',
  'order.cases.bank.LOST': 'Dispute lost',
  'order.section.caseMoney': 'Case decisions',
  'order.cases.column.case': 'Case',
  'order.cases.column.guest': 'To the guest',
  'order.cases.column.bakery': 'Bakery pays',
  'order.cases.column.courier': 'Courier pays',
  'order.cases.column.croissant': 'Croissant pays',
  'order.section.refunds': 'Refunds',
  'order.money.column.when': 'When',
  'order.money.column.what': 'What',
  'order.money.column.amount': 'Amount',
  'order.money.column.status': 'Status',
  'order.section.transfers': 'Transfers',
  'order.transfers.column.party': 'Party',
  'order.transfers.column.target': 'Owed',
  'order.transfers.column.transferred': 'Transferred',
  'order.transfers.column.reversed': 'Taken back',
  'order.transfers.column.state': 'State',
  'order.transfers.party.BAKERY': 'Bakery',
  'order.transfers.party.PARTNER': 'Owner',
  'order.transfers.party.BAKERY_CHARGE': 'Bakery charge',
  'order.transfers.state.OWED': 'Owed',
  'order.transfers.state.SETTLED': 'Settled',
  'order.transfers.state.FAILED': 'Failed',
  'order.section.ratings': 'Ratings',
  'order.rating.DELIVERY': 'Delivery',
  'order.rating.PRODUCTS': 'Products',
  'order.rating.stars': '{stars}/5',
  'order.section.proof': 'Proof of delivery',
  'order.proof.photo': 'Delivery photo',
  'order.proof.signature': 'Signature',
  'order.proof.pin': 'PIN {pin} at {time}',
  'order.support.itemsFlagged': {
    one: '{count} item flagged',
    other: '{count} items flagged',
  },
  'order.support.reply': 'Reply to the guest',
  'order.support.reply.hint':
    'What the guest reads, word for word. Sent through the notification channel; guests cannot answer by SMS.',
  'order.support.send': 'Send the reply',
  'order.support.sent': 'Reply sent to the guest and recorded on the order.',

  'order.action.reassign': 'Reassign bakery',
  'order.action.reassign.noTarget': 'No other shop can take this order.',
  'order.action.reassign.noTarget.house':
    'The guest chose this house, and it has no other shop that can take this order.',
  'order.action.reassign.target': 'New bakery location',
  'order.action.dispatch': 'Dispatch now',
  'order.action.delay': 'Delay pickup',
  'order.action.delayMinutes': 'Delay by (minutes)',
  'order.action.cancelJob': 'Cancel the provider job',
  'order.action.rebook': 'Re-book the provider job',
  'order.action.rebookMinutes': 'Pickup in (minutes)',
  /* Close delivery (issue #1471): an ended order whose job never settled. */
  'order.action.closeDelivery': 'Close delivery',
  'order.action.openCase': 'Open case',
  'order.action.closeDelivery.outcome': 'Outcome',
  'order.action.closeDelivery.outcome.NO_COURIER': 'No courier',
  'order.action.closeDelivery.outcome.FAILED': 'Failed',
  'order.action.closeDelivery.outcome.COMPLETED': 'Delivered',
  'order.action.closeDelivery.note': 'Note',
  'order.action.refund': 'Refund or credit',
  'order.action.done': 'Done',
  /* The guest's invoices on the order view (invoicing.html#delivery). */
  'order.invoices.title': 'Invoices',
  'order.invoices.none': 'Nothing to invoice until the order closes.',
  'order.invoices.create': 'Create invoice',
  'order.invoices.send': 'Send',
  'order.invoices.email': 'Email',
  'order.invoices.sent': 'Sent to {email}',

  // -- refund and liability (SUP-008, PAY-013) ------------------------------
  /* A request opens a review and moves no money (PAY-030, decision 9), so this
     is what happened rather than what was paid. */

  // Why a cascade ended with nobody making the order (AllocationExhaustionCause).
  // These reach the feed through `cause` on ALLOCATION_EXHAUSTED, which renders
  // the union's `kind` - and printed it raw until they were named.
  'cause.NO_ELIGIBLE_BAKERY': 'No bakery could serve it',
  'cause.BAKERY_REJECTED': 'The bakery refused',
  'cause.ACCEPTANCE_TIMEOUT': 'The bakery never answered',
  'cause.ACCESS_FAILURE': 'Access failure',
  'cause.CHARGEBACK': 'Chargeback',
  'cause.GOODWILL': 'Goodwill',
  'cause.PROPERTY_REMOVED': 'Apartment removed',

  /*
   * -- health status (OPS-010, OPS-011, RTE-022) ----------------------------
   *
   * The screen is Health status; the 05:45 check it runs keeps its own name,
   * "pre-service readiness", wherever it is the thing being run - the API
   * endpoint, the NTF-006 alert, OPS-010. One is the board, the other is one
   * of the three things on it.
   */
  'health.title': 'Health status',
  'health.subtitle':
    'Can we serve this morning. The 05:45 pre-service check, the bakery board behind it, and the delivery provider.',
  'health.verdict.green': 'Green - ready for service',
  'health.verdict.red': 'Red - service is not ready',
  'health.passing': '{passed} of {total} conditions passing',
  'health.ranAt': 'last run {time}',
  'health.neverRun': 'this check has not run for this service day',
  'health.run': 'Run the check now',
  'health.runAndNotify': 'Run and send the alert',
  'health.conditions.title': 'Pre-service conditions',
  'health.conditions.subtitle': 'The 05:45 check (OPS-010). Failures are listed first.',
  'health.condition.ALL_ORDERS_ALLOCATED.title': 'Every order is allocated',
  'health.condition.ALL_ORDERS_ALLOCATED.remedy':
    'Allocate or reassign the remaining orders from the dashboard.',
  'health.condition.ALL_ORDERS_ACCEPTED.title': 'Every order is accepted',
  'health.condition.ALL_ORDERS_ACCEPTED.remedy':
    'Call the bakery, or reassign the order to a fallback location.',
  'health.condition.TABLET_HEARTBEATS.title': 'Every bakery tablet is alive',
  'health.condition.TABLET_HEARTBEATS.remedy':
    'Call the location and have the tablet woken; the board below names it.',
  /*
   * Not "the delivery provider answers": nothing is asked of it. The server
   * checks that an adapter is selected and declares capabilities - a `Set`
   * literal in the adapter's own source - so for a provider whose credentials
   * are validated at construction this condition cannot go red at all. Saying
   * "answers" over that was the reason the panel below it looked wrong: the row
   * claimed a conversation nobody had had. The title now claims only what was
   * established, and the delivery provider's real state is the panel's own
   * verdict further down.
   */
  'health.condition.PROVIDER_API_REACHABLE.title': 'A delivery provider is configured',
  'health.condition.PROVIDER_API_REACHABLE.remedy':
    'No adapter is selected, or it declares no capability. This does not prove the provider is reachable - see the delivery provider panel below.',
  'health.condition.WEBHOOK_ENDPOINTS_REACHABLE.title': 'Webhook endpoints answer',
  'health.condition.WEBHOOK_ENDPOINTS_REACHABLE.remedy':
    'Provider callbacks will queue; check the dead-letter view once restored.',
  'health.condition.DEAD_LETTER_EMPTY.title': 'The dead-letter queue is empty',
  'health.condition.DEAD_LETTER_EMPTY.remedy': 'Review and replay the events before service.',
  'health.condition.NO_PENDING_CAPTURES.title': 'No payment capture is pending',
  'health.condition.NO_PENDING_CAPTURES.remedy':
    'A pending capture means an order may be prepared and never paid.',

  'health.arrivals.title': 'Courier arrivals, last 7 days',
  'health.accounting.title': 'Accounting export',
  'health.accounting.configured': 'Pennylane',
  'health.accounting.notConfigured': 'Accounting export not configured',
  'health.arrivals.subtitle':
    'Actual arrival against the prediction the window was built on. Read this before loosening a window or a margin.',
  'health.arrivals.none': 'No delivery in the last {days} days.',
  'health.arrivals.count': 'Deliveries',
  'health.arrivals.median': 'Median',
  'health.arrivals.p90': '90th percentile',
  'health.arrivals.late': 'More than 5 min late',
  'health.arrivals.early': 'More than 5 min early',
  'health.arrivals.minutes': '{minutes} min',
  /*
   * Readiness is about SHOPS. A house owns them and has no counter of its own,
   * so the two sections are titled by mode and listed by shop - the same
   * vocabulary the Bakeries screen already uses for its four tabs.
   */
  'health.bakeries.lastSeenColumn': 'Last seen',
  'health.bakeries.deviceCounts': 'Devices (reachable/total)',
  'health.bakeries.deviceStatus': 'Device status',
  'health.bakeries.bakeryStatus': 'Bakery status',
  'health.bakeries.mode': 'Mode {mode}',
  'health.bakeries.toggleDevices': 'Show or hide devices for {name}',
  'health.bakeries.reachable': 'Reachable — order alerts available',
  'health.bakeries.unreachable': 'Unreachable — cannot receive orders',
  'health.bakeries.deviceUnpaired': 'Unpaired',
  'health.bakeries.deviceUnpairedDetail': 'Cannot receive orders: no paired active device.',
  'health.bakeries.title': 'Active bakeries',
  'health.bakeries.filter.mode': 'Mode',
  'health.bakeries.modeA.title': 'Mode A shops',
  'health.bakeries.modeB.title': 'Mode B shops',
  'health.bakeries.subtitle':
    'A missing tablet heartbeat is an operations alert with the location and last-seen time.',
  'health.bakeries.shop': 'Bakery',
  'health.bakeries.devices': 'Devices',
  'health.bakeries.ordersPaused': 'Orders paused',
  'health.bakeries.closedToday': 'Closed today',
  'health.bakeries.noServiceToday': 'No service today',
  'health.bakeries.serviceHours': 'Pickup from {from} to {to}',
  'health.bakeries.pairedDevices': 'Paired devices: {count}',
  'health.bakeries.none': 'No active bakeries.',
  // The shop's word above the Devices dialog's list, in the approved device
  // words (#2004, P61, P78, P84): what the Devices list and Active bakeries say.
  'pairing.readiness.NOT_EQUIPPED': 'Not equipped',
  'pairing.readiness.PAUSED': 'Paused',
  'pairing.readiness.UNREACHABLE': 'Offline',
  'pairing.readiness.STANDBY': 'Online, in background',
  'pairing.readiness.READY': 'Online',
  /*
   * THE PILL, and it answers the question rather than naming the state
   * (founder, 2026-09-09: "I just need to know clearly if a baker is in a
   * position to take order and if the sunmi will ring"). Two strings, so the
   * column reads down; the colour and the word beside it carry what it costs.
   */
  'health.bakeries.takesOrders': 'Can receive orders',
  'health.bakeries.noOrders': 'Cannot receive orders',
  'health.bakeries.robobakerStandsIn': 'Standing in for a silent Sunmi',
  'health.bakeries.lastSeen': 'Last signal {minutes} min ago',
  'health.bakeries.neverSeen': 'No signal received',
  /*
   * The counter's own board (BAK-002), lane for lane, under the words the
   * baker reads on the device. An operator on the telephone and the baker
   * looking at the tablet have to be naming the same thing.
   */
  'health.bakeries.lane.NEW': 'New',
  'health.bakeries.lane.PREPARE_LATER': 'Scheduled',
  'health.bakeries.lane.PREPARE_NOW': 'Preparing',
  'health.bakeries.lane.READY': 'Ready',
  'health.bakeries.lane.COLLECTED': 'Collected',
  'health.bakeries.lane.ISSUES': 'Issues',
  /* BAK-064: the counter's Cancelled tiles. Not a lane Internal counts or filters on. */
  'health.bakeries.lane.CANCELLED': 'Canceled',
  /** BAK-019 - what the COUNTER changed, not what operations refunded. */
  'health.bakeries.amended': {
    one: '{count} changed for a stock-out',
    other: '{count} changed for a stock-out',
  },
  'health.bakeries.earliestReady': 'First orders ready at {time}',

  'provider.title': 'Delivery provider',
  'provider.active': 'Active provider: {name}',
  'provider.notConfigured': 'No delivery provider set',
  'provider.capabilities': 'Declared capabilities',
  /* What each declared capability DOES, in words (RTE-022): plain text on
     Health status, not pills - a list of what the provider can do (#2004). */
  'providerCapability.SCHEDULED_WINDOWS': 'Scheduled windows',
  'providerCapability.TRAVEL_TIME_ESTIMATE': 'Travel-time estimate',
  'providerCapability.PIN_PROOF': 'PIN proof of delivery',
  'providerCapability.PHOTO_PROOF': 'Photo proof of delivery',
  'providerCapability.SIGNATURE_PROOF': 'Signature proof of delivery',
  'providerCapability.CANCELLATION_WINDOW': 'Cancellation window',
  'providerCapability.TRACKING_URL': 'Live tracking link',
  'providerCapability.QUOTE_EXPIRY': 'Quote expiry',
  'providerCapability.COURIER_TIP': 'Courier tips',
  'providerCapability.MASKED_CONTACT': 'Masked contact number',
  'providerCapability.COURIER_LOCATION': 'Courier location',
  'providerCapability.RESCHEDULE': 'Move a booked window',
  'providerCapability.READY_SIGNAL': 'Bag-ready signal',
  'provider.fallbacks': 'Fallbacks in force',
  'provider.noFallbacks': 'No capability is being substituted.',
  'provider.recentJobs': 'Recent jobs',
  'provider.noJobs': 'No delivery has been dispatched today.',
  'provider.column.order': 'Order',
  'provider.column.status': 'Status',
  'provider.column.quoted': 'Quoted',
  'provider.column.charged': 'Charged',
  'provider.column.divergence': 'Divergence',

  // -- activity (OPS-027) ----------------------------------------------------
  'activity.title': 'Activity',
  'activity.empty': 'Nothing happened in that range.',
  'activity.acknowledged': 'Acknowledged',
  'activity.action.acknowledge': 'Acknowledge',
  'activity.action.open': 'Open',
  'activity.detail.source': 'Source',
  'activity.detail.subjectId': 'Record',
  'activity.detail.changes': 'Changed',
  'activity.detail.fields': 'All fields',
  'activity.rowActions': 'Actions for {title}',
  'activity.filter.severity': 'Severity',
  'activity.filter.from': 'From',
  'activity.filter.to': 'To',
  'activity.filter.code': 'Order code',
  'activity.column.when': 'When',
  'activity.column.what': 'What',
  'activity.column.order': 'Order',
  'activity.column.where': 'Where',
  'activity.column.actor': 'Actor',
  'activity.column.detail': 'Detail',
  'activity.column.actions': 'Actions',
  'activity.summary.placed': 'Placed',
  'activity.summary.delivered': 'Delivered',
  'activity.summary.cancelled': 'Canceled',
  'activity.summary.refused': 'Refused',
  'activity.summary.byCause': 'By cause',
  'activity.summary.declined': 'Cards declined',
  'activity.summary.openAlerts': 'Open alerts',

  'activity.kind.ALERT': 'Alert',
  'activity.kind.ORDER_EVENT': 'Order',
  'activity.kind.REFUSAL': 'Refusal',
  'activity.kind.AUDIT': 'Audit',
  'activity.kind.DEAD_LETTER': 'Dead letter',
  'activity.kind.NOTIFICATION': 'Notification',

  /* Why the platform refused a guest before an order existed (OPS-027). */
  'refusal.WINDOW_NOT_OFFERABLE': 'Window no longer offerable',
  'refusal.WINDOW_PASSED': 'Window already past',
  'refusal.ORDERING_CLOSED': 'Ordering closed',
  'refusal.MINIMUM_BASKET': 'Under the minimum basket',
  'refusal.NO_BAKERY': 'No bakery could serve it',
  'refusal.NOT_SERVABLE': 'Apartment not servable',
  'refusal.SERVICE_PAUSED': 'Service paused',
  'refusal.GUEST_CHOICE_REQUIRED': 'Guest had not chosen a shop',
  'refusal.COURIER_QUOTE_REFUSED': 'Courier would not quote',
  'refusal.COURIER_QUOTE_ABOVE_CEILING': 'Courier quote above the ceiling',
  'refusal.PAYMENT_DECLINED': 'Card declined',

  /* The closed set a counter refuses from (BAK-003). */
  'reject.OUT_OF_STOCK': 'Out of stock',
  'reject.CAPACITY_FULL': 'Capacity full',
  'reject.CLOSED': 'Closed',
  'reject.STAFF_SHORTAGE': 'Short-staffed',
  'reject.EQUIPMENT_FAILURE': 'Equipment failure',
  'reject.OTHER': 'Other',

  /* Every alert kind on the closed list (INTERNAL_ALERT_KINDS in contracts),
     and nothing else: alert-title.ts fails typecheck on a listed kind with no
     title or a title for an unlisted kind. Internal shows each one as
     "Title (CODE)" (founder decision alert-titles = B, #2125). */
  'activity.alert.ACCEPTANCE_TIMEOUT': 'Counter never answered',
  /* #2155: a refused order the engine has not handed on or ended within five
     minutes - usually a courier stand-down the provider keeps refusing. */
  'activity.alert.ASSIGNMENT_STUCK': 'Order waiting for a bakery',
  'activity.alert.BANK_FEED_MATCH_MISSING': 'Baker transfer not found in bank',
  'activity.alert.BAKERY_ISSUE_REPORTED': 'Counter reported an issue',
  'activity.alert.CANCELLATION_INCOMPLETE': 'Cancellation did not finish',
  'activity.alert.CHECKOUT_REFUSALS_DAILY': 'Refusals above normal',
  'activity.alert.CHECKOUT_REFUND_FAILED': 'Checkout refund failed',
  'activity.alert.COUNTER_MUTED': 'Counter muted',
  'activity.alert.COUNTER_UNATTENDED': 'Counter unattended',
  'activity.alert.COURIER_CANCELLED': 'Courier canceled',
  'activity.alert.COURIER_NO_SHOW': 'Courier never came',
  // RTE-035: the courier search. The first is the one per order, at ten
  // minutes, and the order page's Re-book is its action.
  'activity.alert.COURIER_SEARCH_DELAYED': 'No courier after 10 min: book manually',
  'activity.alert.COURIER_SEARCH_REFUSED': 'Courier provider refused the order',
  'activity.alert.COURIER_SEARCH_STOPPED': 'Courier search stopped',
  'activity.alert.COURIER_UNAVAILABLE': 'No courier available',
  'activity.alert.CRON_JOB_FAILED': 'Scheduled job failed',
  'activity.alert.DELIVERY_FAILED': 'Delivery failed',
  // Founder ruling #21: the delivery failed after collection and the bag is
  // on its way back to the counter.
  'activity.alert.DELIVERY_RETURNING': 'Delivery failed, bag returning',
  'activity.alert.DELIVERY_JOB_CANCELLED': 'Delivery job canceled',
  /* RTE-027: the order's net margin before VAT, card fee counted, went below
     zero once the courier's final charge was known. */
  'activity.alert.DELIVERY_LOSS': 'Order lost money on delivery',
  'activity.alert.DELIVERY_RECOVERED': 'Delivery recovered',
  /* RTE-019: every order is one pickup and one drop, so a provider splitting a
     delivery in two means an assumption about the account broke. */
  'activity.alert.DELIVERY_SPLIT': 'Provider split the delivery in two',
  'activity.alert.DEVICE_PIN_LOCKOUTS': 'Counter locked out of its PIN',
  'activity.alert.DEVICE_BATTERY_LOW': 'Counter battery low',
  'activity.alert.DEVICE_CLOCK_SKEW': 'Counter clock is wrong',
  'activity.alert.DEVICE_CRASH_LOOP': 'Counter app crashing repeatedly',
  'activity.alert.DEVICE_PAIRED': 'Device paired',
  'activity.alert.DEVICE_REBOOTED': 'Counter rebooted mid-service',
  'activity.alert.DEVICE_STORAGE_LOW': 'Counter storage nearly full',
  'activity.alert.DEVICE_UPDATE_FAILED': 'Counter update did not install',
  'activity.alert.PRINTER_FAULT': 'Counter printer fault',
  'activity.alert.PRINTER_OUT_OF_PAPER': 'Counter printer out of paper',
  'activity.alert.GUEST_ISSUE_REPORTED': 'Guest reported an issue',
  'activity.alert.GUEST_WHATSAPP_MESSAGE': 'Guest wrote on WhatsApp',
  'activity.alert.WHATSAPP_DELIVERY_FAILED': 'WhatsApp message not delivered',
  'activity.alert.HANDHELD_FORCE_SET': 'Counter build forced on the fleet',
  'activity.alert.HANDHELD_RELEASE_PUBLISHED': 'Counter build published',
  'activity.alert.HANDHELD_ROLLOUT_HALTED': 'Counter rollout halted',
  'activity.alert.KILL_SWITCH_DISENGAGED': 'Kill switch released',
  'activity.alert.KILL_SWITCH_ENGAGED': 'Kill switch engaged',
  'activity.alert.LATENESS_RISK': 'Lateness risk',
  'activity.alert.PAYMENT_CAPTURE_FAILED': 'Capture failed',
  /* G11: Stripe took the guest's money for an order the platform holds as
     unpaid or already ended; nothing will be made, so it is refunded by hand. */
  'activity.alert.PAYMENT_CAPTURED_AFTER_END': 'Paid after the order ended',
  'activity.alert.PAYOUT_FAILED': 'Payout failed',
  'activity.alert.PAYOUT_CANCELED': 'Payout canceled',
  'activity.alert.DOCUMENT_EXPORT_FAILED': 'Document not exported to Pennylane',
  'activity.alert.DOCUMENT_INTEGRITY': 'Invoice file damaged',
  'activity.alert.PAYOUT_OVERDUE': 'Balance unpaid for 60 days',
  'activity.alert.PAYOUT_OVERDUE_ESCALATED': 'Balance unpaid for 80 days',
  'activity.alert.PICKUP_OVER_CAPACITY': 'Pickup hour over capacity',
  'activity.alert.PICKUP_UNCONFIRMED': 'Pickup not confirmed by courier',
  'activity.alert.BALANCE_NEGATIVE': 'Balance negative for 30 days',
  'activity.alert.BALANCE_DRIFT': 'Stripe balance and movements disagree',
  'activity.alert.BALANCE_SETTINGS_DRIFT': 'Balance settings repaired',
  'activity.alert.CONNECTED_ACCOUNT_MISMATCH': 'Stripe account does not match the house',
  'activity.alert.CONNECTED_ACCOUNT_RESTRICTED': 'Stripe restricted a verified account',
  'activity.alert.PROVIDER_CANCEL_FAILED': 'Provider would not cancel',
  'activity.alert.PROVIDER_DISPATCH_REJECTED': 'Provider refused the dispatch',
  'activity.alert.PROVIDER_EVENT_REFUSED': 'Order stopped tracking its delivery',
  /* Follow-up to #1471: the provider reported a pickup or delivery after the
     courier was stood down; the refund is in Disputes. */
  'activity.alert.COLLECTED_AFTER_STAND_DOWN': 'Courier collected after stand-down',
  'activity.alert.PROVIDER_REFUND': 'Provider issued a refund',
  'activity.alert.UNATTACHED_PROVIDER_REFUND': 'Provider refund for a job no order holds',
  'activity.alert.UNATTACHED_DELIVERY_EVENT': 'Courier update for a job no order holds',
  'activity.alert.QR_CAP_RAISE_REQUESTED': 'QR cap raise requested',
  'activity.alert.READINESS_ALERT_UNROUTABLE': 'Readiness alert could not be sent',
  'activity.alert.RECONCILIATION_DRIFT': 'Reconciliation drift',
  'activity.alert.REFUND_FAILED': 'Refund failed, pay the guest another way',
  'activity.alert.REFUND_NEEDS_HUMAN': 'Refund needs a human',
  'activity.alert.REVALIDATION_CANCELLED': 'Apartment removed, order canceled',
  /* Issue #1471: the sweep released a refund case the settlement edge missed. */
  /* Invoicing sections 6 and 7: a guest invoice could not be issued - the
     house's legal block is incomplete, the order's snapshot
     lacks a rate, or the step failed; the alert names which. */
  'activity.alert.GUEST_INVOICE_BLOCKED': 'Guest invoice blocked',
  'activity.alert.SELECTED_DELIVERY_PROVIDER_MISSING': 'Chosen delivery provider is not configured',
  /* RTE-023: no delivery provider is set, so new orders are refused. */
  'activity.alert.DELIVERY_PROVIDER_NOT_CONFIGURED': 'No delivery provider set',
  'activity.alert.REVALIDATION_SUPPLY_AT_RISK': 'Repeat schedules at risk',
  'activity.alert.TABLET_HEARTBEAT_MISSING': 'Tablet heartbeat missing',
  /* #1783: the guest's tip was charged and the forward to the courier
     failed; a timeout may still have paid the courier. */
  'activity.alert.TIP_FORWARD_FAILED': 'Tip charged, forward failed',
  /* #1783: the gateway took a tip an hour ago and no tip row records it. */
  'activity.alert.TIP_CHARGE_UNRECORDED': 'Tip charged, not recorded',
  /* The tip was charged and forwarded to the courier; only its record failed. */
  'activity.alert.TIP_WRITE_INCOMPLETE': 'Tip paid, record missing',
  'activity.alert.TRANSFER_FAILED': 'Transfer failed',
  /* Question 32 (founder, 2026-10-06): a baker's transfer paid from Croissant's
     own balance, which is short for now; retried hourly, never dead-lettered. */
  'activity.alert.TRANSFER_AWAITING_FUNDS': 'Transfer awaiting Croissant funds',
  'activity.alert.UNALLOCATED': 'Nobody can fulfil it',
  /* A stored alert whose kind is missing or no longer listed; its payload
     still carries the code it was written with. */
  'activity.alert.UNCLASSIFIED': 'Unrecognized alert',
  'activity.alert.UNKNOWN_DELIVERY_PROVIDER': 'Order names a delivery provider we cannot reach',

  /* Every transition the order state machine can make (PRD 4.2). */
  'activity.event.PAYMENT_INTENT_CREATED': 'Payment started',
  'activity.event.PAYMENT_AUTHORIZED': 'Payment authorized',
  'activity.event.PAYMENT_AUTHORIZATION_FAILED': 'Payment declined',
  'activity.event.PAYMENT_CAPTURED': 'Payment captured',
  'activity.event.PAYMENT_CAPTURE_FAILED': 'Capture failed',
  'activity.event.PAYMENT_VOIDED': 'Hold released',
  /* The refund case's own annotations, and the provider claim it opens
     (PAY-013, PAY-030). The machine's own review events - LIABILITY_REVIEW_OPENED,
     LIABILITY_DECIDED and REFUND_ISSUED - left the machine and the log's
     readers (register item 51, 2026-09-22): a refund lives in the case record. */
  'activity.event.REFUND_REVIEW_RECORDED': 'Refund case updated',
  'activity.event.REFUND_DECISION_RECORDED': 'Refund decision recorded',
  'activity.event.REFUND_EXECUTION_OBSERVED': 'Refund payment updated',
  'activity.event.REFUND_PROVIDER_CLAIM_OPENED': 'Provider claim opened',
  'activity.event.CLAIM_AMOUNT_SET': 'Provider claim amount set',
  'activity.event.CLAIM_SETTLED': 'Provider claim settled',
  'activity.event.CLAIM_REJECTED': 'Provider rejected the claim',
  'activity.event.CLAIM_ESCALATED': 'Provider claim escalated',
  'activity.event.CLAIM_WRITTEN_OFF': 'Provider claim written off',
  'activity.event.DELIVERY_ESTIMATED': 'Delivery estimated',
  'activity.event.DELIVERY_QUOTED': 'Quoted by the provider',
  'activity.event.DELIVERY_QUOTE_EXPIRED': 'Courier quote expired',
  'activity.event.BAKERY_ALLOCATED': 'Sent to a bakery',
  'activity.event.BAKERY_ACCEPTED': 'Bakery accepted',
  'activity.event.BAKERY_REFUSED': 'Bakery refused',
  /* BAK-044: the two timer events of the offer interval. The clock crossed
     `opensAt`, then `announceFromAt` - nobody raised either. */
  'activity.event.ACCEPTANCE_OPENED': 'Acceptance opened',
  'activity.event.ACCEPTANCE_ANNOUNCED': 'Ringing started',
  'activity.event.ACCEPTANCE_LAPSED': 'No answer from the bakery',
  'activity.event.ASSIGNMENT_RELEASED': 'Taken off the bakery',
  'activity.event.ALLOCATION_EXHAUSTED': 'No bakery could take it',
  'activity.event.PREPARATION_STARTED': 'Preparation started',
  'activity.event.BAG_READY': 'Marked ready',
  'activity.event.BAG_READY_REPORTED_LATE': 'Ready confirmed after collection',
  'activity.event.BAG_CONFIRMED': 'Bag handed over by counter',
  /* BAK-064: OK on the counter's Cancelled tile - older rows only, retired 2026-10-03. */
  'activity.event.CANCELLATION_ACKNOWLEDGED': 'Cancellation seen by the bakery',
  'activity.event.COURIER_BOOKED': 'Courier booked',
  'activity.event.COURIER_BOOKING_REFUSED': 'Courier booking refused',
  'activity.event.COURIER_SEARCH_STARTED': 'Courier search started',
  'activity.event.COURIER_SEARCH_DELAYED': 'Courier search over 10 min',
  'activity.event.COURIER_BOOKING_IN_FLIGHT': 'Courier booking in flight',
  'activity.event.COURIER_BOOKING_REPLY_LOST': 'Courier booking answer lost, retried',
  'activity.event.COURIER_SEARCH_DEADLINE_MOVED': 'Courier search deadline moved',
  'activity.event.COURIER_SEARCH_STOPPED': 'Courier search stopped',
  'activity.event.COURIER_LEFT_BAG': 'Courier left without the bag',
  'activity.event.PROVIDER_COURIER_ASSIGNED': 'Courier assigned',
  'activity.event.PROVIDER_COURIER_WITHDRAWN': 'Courier replaced, same booking',
  'activity.event.PROVIDER_PICKED_UP': 'Picked up by the courier',
  /* GitHub #1299: the same two events, on an order the counter never
     declared ready. The feed is where an operator scanning the morning
     would otherwise see nothing to tell this row from any other delivery
     in transit. */
  'activity.event.PROVIDER_PICKED_UP_UNDECLARED': 'Picked up before the bag was marked ready',
  'activity.event.PROVIDER_COURIER_IMMINENT': 'Courier arriving',
  'activity.event.PROVIDER_COURIER_IMMINENT_UNDECLARED':
    'Courier arriving; the bag was never marked ready',
  'activity.event.PROVIDER_DELIVERED': 'Delivered',
  'activity.event.COURIER_UNAVAILABLE': 'No courier found',
  'activity.event.DELIVERY_FAILED': 'Delivery failed',
  'activity.event.PROVIDER_RETURNING': 'Delivery failed, bag returning',
  'activity.event.PROVIDER_RETURNED': 'Bag back at the counter',
  'activity.event.DELIVERY_CLOSED_BY_OPERATIONS': 'Delivery closed by operations',
  'activity.event.ORDER_CANCELLED': 'Order canceled',
  'activity.event.ESCALATED_TO_MANUAL': 'Escalated to us',
  'activity.event.ORDER_CLOSED': 'Order closed',
  'activity.event.OFF_SESSION_CHARGE_FAILED': 'Off-session charge failed',
  'activity.event.PARTIAL_REFUND_ISSUED': 'Partial refund issued',
  'activity.event.FULL_REFUND_ISSUED': 'Full refund issued',
  'activity.event.COUNTER_DELAY_REQUESTED': 'Pickup delay requested',
  'activity.event.COUNTER_DELAY_PREPARED': 'Pickup delay prepared',
  'activity.event.COUNTER_DELAY_FINISHED': 'Pickup delay reconciled',
  'activity.event.PICKUP_DELAYED': 'Pickup delayed by the counter',
  'activity.event.PROMISE_RESTATED': 'Delivery window set to the booked pickup',
  'activity.event.IMMEDIATE_CASCADE_ANSWERED': 'Counter answered the cascade',
  'activity.event.IMMEDIATE_PICKUP_MOVED': 'Asked the provider to collect sooner',
  'activity.event.BAG_READY_SIGNALLED': 'Bag ready, courier network told',
  'activity.event.FULFILMENT_STOPPED': 'Fulfillment stopped',
  'activity.event.CANCELLATION_AUDIENCE': 'Who was told',
  'activity.event.GUEST_ISSUE_REPORTED': 'Guest reported an issue',
  'activity.event.BAKERY_ISSUE_REPORTED': 'Counter reported an issue',
  'activity.event.LINE_SUBSTITUTION_APPLIED': 'Line replaced or refunded',
  'activity.event.SUPPORT_REPLY_SENT': 'Support replied',
  'activity.event.TIP_CHARGED': 'Guest tipped the courier',
  'activity.event.BAKERY_TRANSFER_MADE': "Baker's balance credited",
  'activity.event.PARTNER_TRANSFER_MADE': "Conciergerie or owner's balance credited",
  'activity.event.TRANSFER_REVERSED': 'Balance credit reversed',
  'activity.event.GATEWAY_REFUND_REPORTED': 'Stripe reported a refund',
  'activity.event.PROVIDER_REFUND_RECORDED': 'Delivery provider credited us',

  /* Dead letters: the queue message that could not be applied (OPS-027 (d)).
     Every one of these used to print its own wire name - `notify.guest_sms`,
     `payment.compensate_void` - into the table, which named the code path and
     not the thing that failed to happen.

     And then every one of them named the ATTEMPT rather than its outcome:
     "Announcing an order is ready", in the SERIOUS band, over a row that says
     the provider refused the dispatch with an HTTP 400. A dead letter exists
     only because something did not happen, so the sentence says so - the
     severity, the error and the title then all report the same event. */
  'activity.deadLetter.delivery.compensate_cancel': 'Could not cancel the courier booking',
  'activity.deadLetter.delivery.delay_pickup': 'Could not reconcile the pickup delay',
  'activity.deadLetter.delivery.pull_pickup': 'Could not move the pickup earlier',
  'activity.deadLetter.delivery.stand_down': 'Could not stand the courier down',
  'activity.deadLetter.delivery.ready_signal':
    'Could not tell the courier network the bag is ready',
  'activity.deadLetter.delivery.timing_observed': 'Could not record the courier timing',
  'activity.deadLetter.delivery.courier_search': 'Could not advance the courier search',
  'activity.deadLetter.delivery.courier_search_check': 'Could not check the courier search booking',
  'activity.deadLetter.webhook.awaiting_booking': 'Could not retry the courier event',
  'activity.deadLetter.finance.connected_account_settings_sync':
    'Could not set the connected-account balance settings',
  'activity.deadLetter.finance.document_deliver': 'Could not email the invoices',
  'activity.deadLetter.finance.document_export': 'Could not export the document to Pennylane',
  'activity.deadLetter.finance.document_store': 'Could not store the document PDF',
  'activity.deadLetter.internal.alert': 'Could not raise an operations alert',
  'activity.deadLetter.internal.kill_switch_changed': 'Could not apply the kill switch',
  'activity.deadLetter.notify.bakery_device_paired': 'Could not confirm the paired device',
  'activity.deadLetter.notify.bakery_open_reminder': 'Could not remind the counter to open',
  'activity.deadLetter.notify.bakery_pairing_code': 'Could not send the pairing code',
  'activity.deadLetter.notify.bakery_payout_onboarding': 'Could not send the onboarding link',
  'activity.deadLetter.notify.bakery_pin': 'Could not send the counter its PIN',
  'activity.deadLetter.notify.guest_email': 'Could not email the guest',
  'activity.deadLetter.notify.guest_whatsapp': 'Could not message the guest on WhatsApp',
  'activity.deadLetter.notify.guest_sms': 'Could not text the guest',
  'activity.deadLetter.notify.readiness_check': 'Could not send the readiness check',
  'activity.deadLetter.notify.site_enquiry': 'Could not pass on a website enquiry',
  'activity.deadLetter.order.dispatched': 'Could not announce the dispatch',
  'activity.deadLetter.order.placed': 'Could not announce the new order',
  'activity.deadLetter.order.ready': 'Could not announce the order was ready',
  'activity.deadLetter.order.stage_advanced': 'Could not announce the delivery stage',
  'activity.deadLetter.order.acceptance_due': 'Could not resolve the acceptance deadline',
  'activity.deadLetter.order.unallocated_refund': 'Could not refund the unallocated order',
  'activity.deadLetter.order.window_expired': 'Could not settle the order past its window',
  'activity.deadLetter.order.money_close': "Could not close the order's money",
  'activity.deadLetter.order.transfers.reconcile':
    "Could not move the baker's, conciergerie's or independent owner's money for the order",
  'activity.deadLetter.order.payments.tag': "Could not tag the order's payment with its cycle",
  'activity.deadLetter.payment.compensate_void': 'Could not void the authorization',
  'activity.deadLetter.payout.bank_details_link': 'Could not send the bank-details link',
  'activity.deadLetter.qr.scanned': 'Could not record the scan',
  'activity.deadLetter.quality.rating_recorded': 'Could not record the rating',
  'activity.deadLetter.refund.execute': 'Could not execute the refund',
  'activity.deadLetter.refund.propose': 'Could not open the refund case',
  'activity.deadLetter.case.open': 'Could not open the Support case',
  'activity.deadLetter.money.reconcile': "Could not carry out the order's money",
  'activity.deadLetter.support.chargeback_evidence': 'Could not file the chargeback evidence',
  'activity.deadLetter.support.credit_requested': 'Could not request the credit',
  'activity.deadLetter.support.ticket_created': 'Could not open the support ticket',

  /* Every message the platform can send, named by its template key (TST-002).
     The row says WHAT was sent; the status beside it says whether it left. */
  'activity.notification.notification.orderConfirmed': 'Order confirmed',
  'activity.notification.notification.preparationIssue': 'Problem with the order',
  'activity.notification.notification.itemReplaced': 'Item replaced',
  'activity.notification.notification.itemRemoved': 'Item removed',
  'activity.notification.notification.pickupDelayed': 'Pickup delayed',
  'activity.notification.notification.onTheWay': 'On the way',
  'activity.notification.notification.arriving': 'Arriving',
  'activity.notification.notification.delivered': 'Delivered',
  'activity.notification.notification.cancelled': 'Order canceled',
  'activity.notification.notification.orderUnfulfilled': 'No counter could take it',
  'activity.notification.notification.refundSentence': 'Refund will be processed',
  'activity.notification.notification.issueResolved': 'Issue resolved',
  'activity.notification.notification.supportReply': 'Support reply',
  'activity.notification.notification.paymentFailed': 'Payment failed',
  'activity.notification.notification.deliveryFailed': 'Delivery failed',
  'activity.notification.notification.courierSearchDelayed': 'Courier search taking longer',
  'activity.notification.notification.dataRequestCode': 'Data request code',
  'activity.notification.bakery.pairingCodeEmail': 'Pairing code by email',
  'activity.notification.bakery.payoutOnboardingEmail': 'Onboarding link by email',
  'activity.notification.bakery.payoutBankDetailsEmail': 'Bank-details link by email',
  'activity.notification.bakery.pairingCodeSms': 'Pairing code by text',
  'activity.notification.bakery.devicePinEmail': 'Counter PIN by email',
  'activity.notification.bakery.devicePinSms': 'Counter PIN by text',
  'activity.notification.bakery.devicePaired': 'Device paired',
  'activity.notification.bakery.openReminder': 'Counter has not started service',
  'activity.notification.internal.latenessRisk': 'Order at risk of being late',
  'activity.notification.internal.unallocatedOrder': 'Order with no bakery',
  'activity.notification.internal.providerFailure': 'Delivery provider failure',
  'activity.notification.internal.webhookOutage': 'Webhook processing behind',
  'activity.notification.internal.readinessCheck': 'Pre-service readiness',
  // NTF-006's other channel. A separate title because the two rows are
  // otherwise identical - same recipient, same status, same reason, and the
  // ledger's channel column has no word for a call.
  'activity.notification.internal.readinessCall': 'Pre-service readiness call',
  'activity.notification.internal.inboundSms': 'Inbound guest text',
  'activity.notification.internal.publicEnquiry': 'Public site enquiry',
  'activity.notification.internal.guestIssue': 'Guest reported a problem',
  'activity.notification.internal.newOrder': 'New-order alert',

  /* Who acted, as a word rather than as the credential the producer stored. */
  'actor.GUEST': 'Guest',
  'actor.BAKERY': 'Counter',
  'actor.SYSTEM': 'Platform',
  'actor.OPERATIONS': 'Operations',
  'actor.SUPPORT': 'Support',
  'actor.OPERATOR': 'Operator',
  'actor.PARTNER': 'Partner',
  'actor.DEVICE': 'Counter device',
  'actor.DEV': 'Development',
  'actor.WEBHOOK': 'Webhook',
  /* The courier network we could not name: an order recorded before its
     provider was written onto it. Every other provider row carries the
     network's own name, through `provider.name` below. */
  'actor.PROVIDER': 'Courier network',

  /* Each provider's name, from the adapter name the order keeps
     (`orders.delivery_provider`). The server never composes a brand: it sends
     the adapter's name and this is where it becomes readable. */
  'provider.name.uber-direct': 'Uber Direct',
  'provider.name.stuart': 'Stuart',
  'provider.name.mock': 'Simulator',

  /* What an administrative act was performed ON, when the platform has no cheap
     name for the record itself. Naming the type is not as good as naming the
     record; it is a great deal better than the uuid that used to be here. */
  'subject.bakery_location': 'A counter',
  'subject.bakery': 'A bakery',
  'subject.property': 'An apartment',
  'subject.partner': 'A partner',
  'subject.partner_member': 'A partner member',
  'subject.order': 'An order',
  'subject.catalogue_item': 'A menu item',
  'subject.rate': 'A commercial rate',
  'subject.rate_card': 'A commission',
  'subject.financial_parameter': 'A financial parameter',
  'subject.qr_code': 'A QR code',
  'subject.pairing_code': 'A pairing code',
  'subject.kill_switch': 'The kill switch',
  'subject.guest_phone': 'A guest number',
  'subject.bulk_action': 'A bulk action',
  'subject.dead_letter': 'A dead letter',
  'subject.alert': 'An alert',
  /* The subject types the platform writes that had no name here, so the
     Where column printed `platform_caps` at an operator. The last four are
     what a dead letter is about when it is about none of the three the feed
     has columns for. */
  'subject.catalogue_pool': 'The shared menu',
  'subject.platform_caps': 'The platform caps',
  'subject.platform_ordering': 'The ordering modes',
  'subject.platform_guest_support': 'The guest support numbers',
  'subject.platform_whatsapp': 'WhatsApp',
  'subject.platform_delivery': 'The delivery provider',
  'subject.handheld_release': 'A counter build',
  'subject.handheld_update': 'The counter update state',
  'subject.device': 'A counter device',
  'subject.platform_handheld': 'The counter fleet settings',
  'subject.payout': 'A payout',
  'subject.delivery_statement': 'A delivery statement',
  'subject.payment': 'A payment',
  'subject.platform': 'The platform',
  'subject.reconciliation': 'A reconciliation',
  'subject.site_enquiry': 'A website enquiry',
  'subject.schedule': 'A repeat stay',
  'subject.issue_report': 'A reported issue',

  /* Payload field names, so a row reads as words rather than as the producer's
     own camelCase. Unlisted fields fall back to the field name, which is what
     keeps an unfamiliar producer readable. */
  'field.label': 'Name',
  'field.name': 'Name',
  'field.email': 'Email',
  'field.reason': 'Reason',
  'field.packageNotReady': 'Bag not ready',
  'field.reference': 'Reference',
  'field.cause': 'Cause',
  'field.guestTold': 'Guest',
  'field.counterTold': 'Counter',
  'field.providerTold': 'Delivery company',
  'field.partnerTold': 'Conciergerie or owner',
  'field.operationsTold': 'Operations',
  // The order-event fields the feed prints and had no label for, so they
  // reached an operator as the producer's own camelCase beside translated
  // neighbours: `opensAt`, `answerBy`, `onNoAnswer`, `fee`, `etaMinutes`,
  // `extent`, `axis`.
  // Every order-event payload field the feed prints, so none of them reaches
  // an operator as the producer's own camelCase (swept against the demo world).
  'field.leg': 'Leg',
  'field.proof': 'Proof of delivery',
  'field.finalFee': 'Courier fee billed',
  'field.jobRef': 'Job at the provider',
  'field.quotedFee': 'Quoted courier fee',
  'field.how': 'Accepted',
  'field.prepMinutes': 'Prep minutes',
  'field.moved': 'Moved by the provider',
  'field.collectReady': 'Collect from',
  'field.quoteRef': 'Quote',
  'field.expiresAt': 'Expires',
  'field.answer': 'Answer',
  'field.seconds': 'Seconds',
  'field.decision': 'Decision',
  /* Who the operator held responsible for a refund. It rode on the audit row
     alone until the live loop read a refunded order back and found the party
     nowhere on it, so the decision's own event carries it now (R1-D2). */
  'field.party': 'Responsible party',
  'field.basis': 'Booked on',
  /* `COURIER_BOOKED.requestedAt`: when the platform asked the provider for the
     courier. The event's own time is when the provider's create answered. */
  'field.requestedAt': 'Booking requested',
  'field.productId': 'Product',
  'field.removedUnits': 'Units removed',
  'field.bakedQuantity': 'Units baked',
  'field.priceDeltaMinor': 'Price difference',
  'field.reasonCode': 'Reason',
  'field.note': 'Note',
  /* The three fields the September 2026 machine put on an order event:
     which of the two things an allocation is, what a cancellation says
     happened, and the refusal a cancelling write is answering. */
  'field.entry': 'Entry',
  'field.cancelledCause': 'What happened',
  'field.refusal': 'Refusal',
  'field.amount': 'Amount',
  'field.refunded': 'Refunded',
  // The refunds-queue item a counter's line adjustment was booked onto, and
  // what that item stands at once this adjustment is on it (R4-D4).
  'field.reviewRef': 'Refund item',
  /* THE REVIEW THE ROW IS ABOUT, named by the writer rather than counted by
     the reader. A review's identity used to be re-derived by counting rows at
     five call sites, and two of those counts disagreeing spent one gateway key
     twice (R5-D2, R6-A3). */
  'field.ref': 'Refund item',
  'field.number': 'Number',
  /* Where the money came to rest, computed from the ledger rather than read
     off the operator's word for it (R5-A3, R6-A6). */
  'field.resting': 'Payment now',
  'field.owedAmount': 'Owed to guest',
  'field.owedOnReview': 'Owed on this item',
  /* What the review was standing at when the operator answered it (R7-A1).
     A decision that returns nothing moves nothing, so without this figure
     on the row nothing downstream could tell that those units had been
     answered - and the next review offered them again. */
  'field.owed': 'Asked about',
  'field.minutes': 'Minutes',
  'field.minutesLate': 'Minutes late',
  'field.waitedMinutes': 'Minutes waited',
  'field.windowStart': 'Window',
  'field.basketValueMinor': 'Basket',
  'field.currency': 'Currency',
  'field.declineCode': 'Decline code',
  'field.declineReason': 'Decline reason',
  'field.nextOfferableStart': 'Next offerable',
  'field.shortfall': 'Shortfall',
  'field.fromStatus': 'From',
  'field.toStatus': 'To',
  'field.storefrontMode': 'Storefront mode',
  'field.fulfillmentMode': 'Fulfillment mode',
  'field.lastSeenAt': 'Last seen',
  'field.latestPickup': 'Latest pickup',
  'field.firstPickupLocalTime': 'First pickup',
  'field.orderCount': 'Orders',
  'field.refusals': 'Refusals',
  'field.threshold': 'Threshold',
  'field.smsSent': 'Text sent',
  'field.lastError': 'Error',
  'field.attempts': 'Attempts',
  'field.replayedAt': 'Replayed',
  'field.source': 'Source',
  'field.messageType': 'Message',
  'field.kind': 'Kind',
  'field.keyId': 'Key',
  'field.deviceId': 'Device',
  'field.viaPairing': 'Via pairing',
  'field.subjectType': 'Subject type',
  'field.subjectId': 'Subject',
  'field.requestId': 'Request',
  'field.guestKeyHash': 'Guest key',
  'field.engaged': 'Engaged',
  'field.confirmed': 'Confirmed',
  'field.change': 'Change',
  'field.effectiveAt': 'Effective',
  'field.geocodeSource': 'Geocoded by',
  'field.salePrice': 'Sale price',
  'field.channel': 'Channel',
  'field.scope': 'Scope',
  'field.affected': 'Affected',
  'field.skipped': 'Skipped',
  'field.created': 'Created',
  'field.updated': 'Updated',
  'field.issuedAt': 'Issued',
  'field.hadCode': 'Had a code',
  'field.bagCount': 'Bags',
  'field.appVersion': 'App version',
  'field.self': 'By itself',
  'field.byOperator': 'By an operator',
  'field.resetAt': 'Reset',
  'field.pruned': 'Pruned',
  'field.unseenSince': 'Unseen since',
  'field.transitioned': 'Moved',
  'field.pool': 'Pool',
  /* The payload field names the demo world actually carries that had no
     entry here, so a row printed the producer's own camelCase key - and the
     French portal printed that key in English. */
  'field.acceptance': 'Acceptance',
  'field.acceptanceMode': 'Acceptance mode',
  'field.acceptancePolicy': 'Acceptance policy',
  'field.acceptanceTimeoutMinutes': 'Acceptance timeout',
  'field.active': 'Active',
  'field.actor': 'Actor',
  'field.addressLine1': 'Address',
  'field.alerts': 'Alerts',
  'field.allergens': 'Allergens',
  'field.amountMinor': 'Amount',
  'field.availability': 'Availability',
  'field.bagPhotoPolicy': 'Bag photos',
  'field.bakeryLocationId': 'Shop',
  'field.bakeryOrgId': 'Bakery',
  'field.boundAt': 'Bound',
  'field.city': 'City',
  'field.code': 'Code',
  'field.commissionBps': 'Commission',
  'field.contactEmail': 'Contact email',
  'field.contactMobile': 'Contact mobile',
  'field.contactPhone': 'Contact phone',
  'field.courier': 'Courier',
  'field.defaultDeliveryInstructions': 'Delivery instructions',
  'field.defaultStorefrontMode': 'Default storefront mode',
  'field.delayMinutes': 'Delay',
  'field.description': 'Description',
  'field.displayName': 'Display name',
  'field.durationMinutes': 'Duration',
  'field.earliestReady': 'Earliest ready',
  'field.escalationEmail': 'Escalation email',
  'field.escalationPhone': 'Escalation phone',
  'field.failures': 'Failures',
  'field.firstConfirmation': 'First confirmation',
  'field.generated': 'Generated',
  'field.guestApproved': 'Guest approved',
  'field.guestSelected': 'Guest selected',
  'field.imageryCount': 'Images',
  'field.immediate': 'Immediate',
  'field.issueReportId': 'Issue report',
  'field.layout': 'Layout',
  'field.leadTimeMinutes': 'Lead time',
  'field.lineIndex': 'Line',
  'field.lineIndexes': 'Lines',
  'field.lines': 'Lines',
  'field.locale': 'Language',
  'field.logoRef': 'Logo',
  'field.maxBasketItems': 'Max items',
  'field.maxBasketValue': 'Max basket',
  'field.maxOrdersPerBakeryPerDay': 'Max orders per shop per day',
  'field.maxCourierQuote': 'Max courier quote',
  'field.maxOrdersPerWindow': 'Max orders per window',
  'field.minimumBasket': 'Minimum basket',
  'field.order': 'Order',
  'field.orderId': 'Order',
  'field.payoutVerification': 'Payout verification',
  'field.photoId': 'Photo',
  'field.photoRef': 'Photo',
  'field.postalCode': 'Postal code',
  'field.preorder': 'Pre-order',
  'field.promisedWindowStart': 'Promised window',
  'field.promiseMovedByMinutes': 'Promise moved by',
  'field.propertyId': 'Apartment',
  'field.providerJobRef': 'Job at the provider',
  'field.publicId': 'Public id',
  'field.qrGenerationCap': 'QR cap',
  'field.quotedProviderFee': 'Quoted courier fee',
  'field.rank': 'Rank',
  'field.rateVersionCut': 'Rate version',
  // Rows written before round 6 (R6-A5) carry this name for a figure that was
  // OWED rather than refunded; `order_events` is append-only, so the key stays.
  'field.refundedAmount': 'Refunded',
  'field.refundedTotal': 'Refunded total',
  'field.refundedTotalMinor': 'Refunded total',
  'field.replacedWithItemId': 'Replaced with',
  'field.replacementId': 'Replacement',
  'field.role': 'Role',
  'field.serviceRadiusMeters': 'Service radius',
  'field.smallOrderFee': 'Small-order fee',
  'field.smallOrderFeeThreshold': 'Small-order threshold',
  'field.status': 'Status',
  'field.supersededJobRef': 'Superseded job',
  'field.suspended': 'Suspended',
  'field.text': 'Text',
  'field.ticketRef': 'Ticket',
  'field.takenAt': 'Taken',
  'field.timeZone': 'Time zone',
  'field.type': 'Type',
  'field.updatedAt': 'Updated',
  'field.updatedBy': 'Updated by',
  'field.which': 'Which',
  'field.observedAt': 'Recorded at',
  'field.off': 'Off',
  'field.until': 'Until',
  'field.properties': 'Apartments',
  'field.savedAsContact': 'Saved as contact',
  'field.voidedAuthorization': 'Authorization voided',
  'field.outcome': 'Outcome',
  'field.acknowledged': 'Acknowledged',
  /* The rest of what a producer's payload can put in front of an operator.
     `tCode` falls back to the KEY, silently, so an unlisted field reads as
     `onNoAnswer` or `chargeDrift` on the row - a machine word at a human, and
     31 of the 49 keys the demo feed carries were doing exactly that. */
  'field.acknowledgedAt': 'Acknowledged at',
  'field.acknowledgedBy': 'Acknowledged by',
  'field.after': 'After',
  'field.announceFromAt': 'Ringing from',
  'field.answerBy': 'Answer expected by',
  'field.at': 'At',
  'field.axis': 'Axis',
  'field.before': 'Before',
  'field.cancelled': 'Canceled',
  'field.chargeDrift': 'Charge drift',
  'field.day': 'Day',
  'field.detail': 'Detail',
  'field.deviceLabel': 'Device',
  'field.documentId': 'Document',
  'field.durationMs': 'Duration',
  'field.etaMinutes': 'Estimated travel time',
  'field.extent': 'Extent',
  'field.fee': 'Courier fee',
  'field.locationId': 'Shop',
  'field.method': 'Method',
  'field.network': 'Network',
  /* #2155: a refusal a closed day made, rather than a tap. */
  'field.occasion': 'Occasion',
  'field.onNoAnswer': 'If no answer',
  'field.opensAt': 'Offered at',
  /* #2155: how many orders a closed day took - one total, never a split. */
  'field.orders': 'Orders',
  'field.organizationId': 'Organization',
  'field.partnerOrgId': 'Partner',
  'field.path': 'Path',
  'field.publishedBy': 'Published by',
  'field.raisedAt': 'Raised at',
  'field.refundedMinor': 'Refunded',
  'field.refused': 'Refused',
  'field.serviceDate': 'Service day',
  'field.transferDrift': 'Transfer drift',
  'field.versionCode': 'Version code',
  'field.versionName': 'Version',

  /*
   * The fourteen fields an order's timeline could print with no name at all:
   * `tCode` falls back to the key itself, silently, so a lateness alert showed
   * "promisedArrival", "estimatedArrival" and "latenessRiskMinutes" in
   * camelCase on a page of sentences (founder, 2026-09-07).
   *
   * A closed and checked list: it comes from the payloads the order machine
   * writes and from the alerts that name an `orderId` - the only two things
   * this card renders.
   */
  'field.promisedArrival': 'Promised arrival',
  'field.estimatedArrival': 'Estimated arrival',
  'field.latenessRiskMinutes': 'Expected lateness',
  'field.chargedMinor': 'Amount charged',
  'field.heldMinor': 'Amount held',
  'field.delivery': 'Delivery state',
  'field.error': 'Error',
  'field.eventKind': 'Event type',
  'field.issueKind': 'Issue type',
  'field.itemsFlagged': 'Lines flagged',
  'field.photo': 'Photo attached',
  'field.providerName': 'Provider',
  'field.orderState': 'Order',
  /* The other three instants the courier was told, on the booking
     (COURIER_BOOKED); `collectReady` is titled above, beside the pull that
     moves it. */
  /* The quote an estimate holds for the booking (RTE-027 as amended). */
  'field.quoteExpiresAt': 'Quote expires',
  'field.collectDeadline': 'Collect by',
  'field.deliverReady': 'Deliver from',
  'field.deliverDeadline': 'Deliver by',
  /* The two records a producer writes as one nested field - the courier
     (`PROVIDER_COURIER_ASSIGNED`) and the proof of delivery
     (`PROVIDER_DELIVERED`). Both reached the screen as truncated JSON until
     `flattened` opened them out; these are the fields inside. */
  'field.firstName': 'Courier',
  'field.vehicleType': 'Vehicle',
  'field.vehicleMake': 'Make',
  'field.vehicleModel': 'Model',
  'field.vehicleColour': 'Color',
  'field.licensePlate': 'Plate',
  'field.maskedPhone': 'Masked line',
  'field.maskedPhonePin': 'Line PIN',
  'field.trackingUrl': 'Tracking page',
  'field.photoUrl': 'Photo',
  'field.signatureUrl': 'Signature',
  'field.locationUpdatedAt': 'Position taken at',
  'field.location': 'Position',
  'field.packageId': 'Package ID',
  'field.capturedAt': 'Captured at',
  'field.start': 'Window start',
  'field.end': 'Window end',
  'field.previousStart': 'Previous window start',
  'field.previousEnd': 'Previous window end',
  'field.signalled': 'Sent to the provider',
  'field.movementId': 'Movement',
  'field.transferRef': 'Stripe transfer',
  'field.sourceChargeRef': 'Stripe charge',
  'field.family': 'Document family',
  'field.nature': 'Document type',
  'field.accountingState': 'Accounting state',
  'field.deliveredAt': 'Handed over at',
  'field.pin': 'PIN given',
  'field.readyAt': 'Ready at',
  'field.repliedBy': 'Replied by',

  /*
   * Values that are themselves codes (CODED_DETAIL_FIELDS).
   *
   * A member of a closed set is spelled out, or the line asks a question in
   * words and answers it in an enum: "If no answer CASCADE" read as an answer
   * the bakery had failed to give, when it is the standing instruction for
   * what happens if nobody answers by the deadline on the line above it.
   */
  'onNoAnswer.CASCADE': 'Offer it to another bakery',
  'onNoAnswer.AUTO_ACCEPT': 'Accept it automatically',
  'onNoAnswer.ESCALATE': 'Escalate to us',
  'acceptance.MANUAL': 'By the counter',
  'acceptance.AUTO': 'Automatic',
  'bagPhotos.OFF': 'Off',
  'bagPhotos.OPTIONAL': 'Optional',
  'bagPhotos.MANDATORY': 'Mandatory',
  'bookingBasis.QUOTE': "The provider's quote",
  'bookingBasis.PRICE': 'The expected courier price',
  'leg.PICKUP': 'Pickup',
  'leg.DROPOFF': 'Dropoff',
  'axis.payment': 'Payment',
  'axis.assignment': 'Assignment',
  'axis.bag': 'Bag',
  'axis.delivery': 'Delivery',
  'axis.order': 'Order',
  'axis.claim': 'Claim',
  /* The three region names only HISTORY holds: rows written before the
     four-region machine. Never written again; read for ever. */
  'axis.lifecycle': 'Order',
  /*
   * What kind of record a line of the order's debug log is (#2004): plain
   * text in the log's kind column, not a pill - it says what the line IS, not
   * how anything stands. A transition names its region (`axis.*` above).
   */
  'eventSource.OUTBOUND': 'API',
  'eventSource.WEBHOOK': 'Webhook',
  'eventSource.EVENT': 'Event',
  'eventKind.ALERT': 'Alert',
  'eventKind.NOTIFICATION': 'Notice',
  'eventKind.DEAD_LETTER': 'Dead-letter',
  'eventKind.REFUSAL': 'Refusal',
  'eventKind.AUDIT': 'Audit',
  'eventKind.ORDER_EVENT': 'Event',
  /*
   * Where a region stood, in words (PRD 4.2). ONE flat family for all four
   * regions: a state name is unique enough within the machine that the word
   * is the same wherever it appears (`FAILED` is failed on the payment and on
   * the delivery), and the axis is printed beside it on every line that uses
   * this. Read by the order's timeline and by the feed's detail through
   * `tCode('lifecycle', state)`.
   */
  'lifecycle.CREATED': 'Created',
  'lifecycle.PENDING': 'Payment pending',
  'lifecycle.PAID': 'Paid',
  'lifecycle.ABANDONED': 'Abandoned',
  'lifecycle.FAILED': 'Failed',
  /* RETIRED, AND KEPT. `order_events` is append-only and stores the state as
     text, so an order the previous release moved into LIABILITY_REVIEW still
     prints that word on its own timeline. Its successor REFUND_REVIEW, and the
     four payment states after it, left the machine altogether (register item
     51, 2026-09-22): a refund rests in the case record, not on the order. */
  'lifecycle.LIABILITY_REVIEW': 'Under liability review',
  /* BAK-044: the offer interval, split in three by the two bits the machine
     owns an instant for - may the counter answer, and is it being rung. The
     one word it replaced, and the `ACCEPTED` that used to follow it, are both
     gone from the region, so neither has a row left to name. */
  'lifecycle.SCHEDULED': 'Scheduled',
  'lifecycle.ACCEPTANCE_OPEN': 'Open to answer',
  'lifecycle.ACCEPTANCE_CALLING': 'Ringing at the counter',
  'lifecycle.PREPARING': 'Preparing',
  'lifecycle.READY': 'Ready',
  /* #2155: refused, and waiting for the every-minute engine to offer it to
     another house or end it. Its words are the summary's, because no surface
     shows the position as a state of its own (decision 3). */
  'lifecycle.AWAITING_ASSIGNMENT': 'Awaiting bakery',
  'lifecycle.COLLECTED': 'Collected',
  'lifecycle.DELIVERED': 'Delivered',
  'lifecycle.DELIVERY_FAILED': 'Delivery failed',
  'lifecycle.REFUSED': 'Refused',
  'lifecycle.NO_ANSWER': 'No answer',
  'lifecycle.EXHAUSTED': 'No bakery left',
  'lifecycle.CANCELLED': 'Canceled',
  'lifecycle.CLOSED': 'Closed',
  'lifecycle.NOT_QUOTED': 'Not priced',
  'lifecycle.ESTIMATED': 'Estimated',
  'lifecycle.QUOTED': 'Quoted',
  'lifecycle.QUOTE_EXPIRED': 'Quote expired',
  'lifecycle.BOOKED': 'Courier booked',
  'lifecycle.COURIER_ASSIGNED': 'Courier assigned',
  'lifecycle.AT_BAKER': 'Courier at the counter',
  'lifecycle.IN_TRANSIT': 'On the way',
  'lifecycle.ARRIVING': 'Arriving',
  'lifecycle.COMPLETED': 'Delivered',
  'lifecycle.NO_COURIER': 'No courier',
  'lifecycle.RETURNING': 'Bag returning',
  'lifecycle.RETURNED': 'Bag returned',
  'lifecycle.NONE': 'No claim',
  'lifecycle.OPEN': 'Open',
  'lifecycle.SETTLED': 'Settled',
  'lifecycle.REJECTED': 'Rejected',
  'lifecycle.ESCALATED': 'Escalated',
  'lifecycle.WRITTEN_OFF': 'Written off',

  /* Yes, no, and a duration with its unit: "Guest selected false" and
     "Estimated travel time 19" are values a reader has to decode. */
  'value.yes': 'Yes',
  'value.no': 'No',
  'value.minutes': '{count} min',

  /* Administrative acts (OPS-008). Every action an audit row can name. */
  'activity.audit.bakery.capacity_changed': 'Preparation capacity changed',
  'activity.audit.bakery.bag_photo_added': 'Bag photo added',
  'activity.audit.bakery.closure_recorded': 'Closure recorded',
  'activity.audit.bakery.closure_removed': 'Closure removed',
  'activity.audit.bakery.collected_confirmed': 'Collection confirmed',
  'activity.audit.bakery.counter_reachable': 'Counter reachable again',
  'activity.audit.bakery.counter_unreachable': 'Counter unreachable',
  'activity.audit.bakery.courier_no_show_flagged': 'Courier no-show flagged',
  'activity.audit.bakery.device_paired': 'Device paired',
  'activity.audit.bakery.device_identity_adopted': 'Device moved to its permanent ID',
  'activity.audit.device.version_changed': 'Counter build changed',
  'activity.audit.device.rebooted': 'Counter rebooted',
  'activity.audit.device.update_installed': 'Counter update installed',
  'activity.audit.device.update_failed': 'Counter update failed',
  'activity.audit.device.command_issued': 'Command sent to counter',
  'activity.audit.device.command_acknowledged': 'Counter answered a command',
  'activity.audit.handheld.config_updated': 'Counter configuration changed',
  'activity.audit.device.crashed': 'Counter crash reported',
  'activity.audit.device.diagnostics_uploaded': 'Counter diagnostics uploaded',
  'activity.audit.device.renamed': 'Counter renamed',
  'activity.audit.device.retired': 'Counter retired',
  'activity.audit.device.request_failed': 'Counter request failed',
  'activity.audit.bakery.device_revoked': 'Device revoked',
  'activity.audit.bakery.order_issue_flagged': 'Order issue flagged',
  'activity.audit.bakery.orders_paused_unattended': 'Orders paused, counter unattended',
  'activity.audit.bakery.orders_paused': 'Orders paused by the counter',
  'activity.audit.bakery.orders_resumed': 'Orders resumed',
  'activity.audit.bakery.pairing_refused': 'Pairing refused',
  'activity.audit.bakery.pairing_regenerated': 'Pairing code regenerated',
  'activity.audit.bakery.pickup_delayed': 'Pickup delayed by the counter',
  'activity.audit.bakery.pin_reset': 'PIN reset',
  'activity.audit.bakery.pin_set': 'PIN set',
  'activity.audit.bakery.service_started': 'Service started',
  'activity.audit.bakery.settings_changed': 'Counter settings changed',
  'activity.audit.bakery.stock_changed': 'Stock changed',
  'activity.audit.bakery.stock_out_resolved': 'Stock-out resolved',
  'activity.audit.delivery.rescheduled': 'Delivery rescheduled',
  'activity.audit.internal.alert_acknowledged': 'Alert acknowledged',
  'activity.audit.internal.anomaly_acknowledged': 'Anomaly acknowledged',
  'activity.audit.internal.bakery_location_created': 'Shop created',
  'activity.audit.internal.bakery_location_deleted': 'Shop deleted',
  'activity.audit.internal.bakery_location_updated': 'Shop updated',
  'activity.audit.internal.bakery_price_agreed': 'Bakery price agreed',
  'activity.audit.internal.bakery_reassigned': 'Bakery reassigned',
  'activity.audit.internal.caps_updated': 'Caps updated',
  'activity.audit.internal.catalogue_item_created': 'Menu item created',
  'activity.audit.internal.catalogue_item_updated': 'Menu item updated',
  'activity.audit.internal.catalogue_reordered': 'Menu reordered',
  'activity.audit.internal.product_type_created': 'Kind created',
  'activity.audit.internal.product_type_updated': 'Kind renamed',
  'activity.audit.internal.product_type_deleted': 'Kind deleted',
  'activity.audit.internal.product_types_reordered': 'Kinds reordered',
  'activity.audit.internal.formule_composed': 'Formule composition changed',
  'activity.audit.internal.formule_repriced': 'Set repriced from its parts',
  'activity.audit.internal.set_house_price_derived': 'Set bakery price set from its products',
  'activity.audit.internal.dead_letter_discarded': 'Dead letter discarded',
  'activity.audit.internal.dead_letter_replayed': 'Dead letter replayed',
  'activity.audit.internal.delivery_cancel_job': 'Delivery job canceled',
  'activity.audit.internal.delivery_close': 'Delivery closed',
  'activity.audit.internal.delivery_delay_pickup': 'Pickup delayed',
  'activity.audit.internal.delivery_provider_updated': 'Delivery provider changed',
  'activity.audit.internal.guest_sms_updated': 'Guest SMS switched',
  'activity.audit.internal.first_payout_reviewed': 'First payout reviewed',
  'activity.audit.internal.monitoring_thresholds_updated': 'Monitoring thresholds updated',
  'activity.audit.internal.handheld_thresholds_updated': 'Counter alert thresholds updated',
  'activity.audit.internal.handheld_canary_updated': 'Counter canary shops updated',
  'activity.audit.internal.notification_resent': 'Notification resent',
  'activity.audit.internal.ordering_modes_updated': 'Ordering modes updated',
  'activity.audit.internal.guest_support_updated': 'Guest support numbers updated',
  'activity.audit.internal.accounting_settings_updated': 'Pennylane settings updated',
  'activity.audit.internal.document_export_retried': 'Pennylane export retried',
  'activity.audit.internal.whatsapp_recipients_updated': 'New-order alert numbers updated',
  'activity.audit.internal.whatsapp_templates_submitted': 'WhatsApp templates submitted to Meta',
  'activity.audit.internal.org_created': 'Organization created',
  'activity.audit.internal.org_default_mode_assigned': 'Default storefront mode assigned',
  'activity.audit.internal.org_deleted': 'Organization deleted',
  'activity.audit.internal.org_updated': 'Organization updated',
  'activity.audit.internal.property_mode_assigned': 'Apartment storefront mode assigned',
  'activity.audit.internal.rate_changed': 'Commercial rate changed',
  'activity.audit.internal.rate_card_changed': 'Commission changed',
  'activity.audit.internal.financial_parameter_changed': 'Financial parameter changed',
  'activity.audit.internal.refund_declined': 'Refund declined',
  'activity.audit.internal.refund_issued': 'Refund issued',
  'activity.audit.support.case_bank_outcome': 'Bank ruled on the dispute',
  'activity.audit.support.case_dispute_fee': 'Dispute fee recorded',
  /* The operator's own act, as against the order's event. A request for a
     judged cause asks a question and moves no money (PAY-030), so it gets its
     own row rather than borrowing "Refund issued" for something that did not
     happen. */
  'activity.audit.internal.liability_review_requested': 'Refund review opened',
  'activity.audit.internal.liability_decided': 'Refund review decided',
  /* The refunds queue's own row: what the OPERATOR confirmed, which may be
     several orders in one command. The order's own event is written by the
     decision itself. */
  'activity.audit.internal.refund_confirmed': 'Refund confirmed',
  /* No producer writes these four since the founder's 2026-10-08 decision
     (claim-move-two-activity-rows = B): a claim move writes only the order's
     own order.claim_<action> row. The titles stay because rows already
     written keep their code - the demo database held twelve of them when read
     that day (settle 5, escalate 3, reject 3, write_off 1) - and the feed's
     fallback for an unknown code is the raw code. */
  'activity.audit.internal.claim_settle': 'Provider claim settled',
  'activity.audit.internal.claim_reject': 'Provider claim rejected',
  'activity.audit.internal.claim_escalate': 'Provider claim escalated',
  'activity.audit.internal.claim_write_off': 'Provider claim written off',
  'activity.audit.internal.substitution_mapping_set': 'Substitution mapping set',
  'activity.audit.internal.support_reply_sent': 'Support reply sent',
  'activity.audit.order.liability_review_opened': 'Refund review opened',
  'activity.audit.order.liability_decided': 'Refund review decided',
  'activity.audit.order.claim_settle': 'Provider claim settled',
  'activity.audit.order.claim_reject': 'Provider claim rejected',
  'activity.audit.order.claim_escalate': 'Provider claim escalated',
  'activity.audit.order.claim_write_off': 'Provider claim written off',
  'activity.audit.order.refund_owed': 'Refund owed',
  'activity.audit.order.bakery_transfer_made': "Baker's balance credited",
  'activity.audit.order.partner_transfer_made': "Conciergerie or owner's balance credited",
  'activity.audit.order.transfer_reversed': 'Balance credit reversed',
  'activity.audit.order.refunded': 'Order refunded',
  'activity.audit.order.stranded_refund': 'Stranded order refunded',
  'activity.audit.order.silent_courier': 'Silent courier: delivery failed',
  'activity.audit.order.support_cancelled': 'Order canceled by support',
  'activity.audit.order.unallocated_refund': 'Unallocated order refunded',
  // The order-less compensation: a guest charged for an order whose write
  // failed, and the operator's answer to it (resolution 28).
  'activity.audit.payment.compensation_owed': 'Compensation owed',
  'activity.audit.payment.compensation_decided': 'Compensation decided',
  // The thin refund case: opened at the order's final state or on a later
  // problem, decided by an operator, paid through Stripe.
  'activity.audit.refund.case_opened': 'Refund case opened',
  'activity.audit.settlement.item_decided': 'Settlement item decided',
  'activity.audit.settlement.refund_not_executed': 'Refund recorded as not executed',
  'activity.audit.refund.case_reported': 'Problem reported',
  'activity.audit.refund.case_noted': 'Case note added',
  'activity.audit.refund.case_decided': 'Refund case decided',
  'activity.audit.refund.case_paid': 'Refund paid',
  'activity.audit.refund.case_failed': 'Refund failed',
  'activity.audit.refund.case_superseded': 'Case superseded by a chargeback',
  'activity.audit.refund.dispute_status': 'Chargeback status changed',
  'activity.audit.partner.catalogue_item_created': 'Menu item created by the partner',
  'activity.audit.partner.catalogue_item_updated': 'Menu item updated by the partner',
  'activity.audit.partner.formule_repriced': 'Set repriced from its parts by the partner',
  'activity.audit.partner.set_house_price_derived':
    'Set bakery price set from its products by the partner',
  'activity.audit.partner.location_created': 'Location created',
  'activity.audit.partner.legal_identity_updated': 'Legal identity updated',
  'activity.audit.partner.connected_account_retired': 'Stripe account retired',
  'activity.audit.partner.member_invited': 'Member invited',
  'activity.audit.partner.member_updated': 'Member updated',
  'activity.audit.partner.member_removed': 'Member removed',
  'activity.audit.partner.org_provisioned': 'Organization provisioned',
  'activity.audit.partner.org_renamed': 'Organization renamed',
  'activity.audit.partner.external_id_recorded': 'Organization id recorded',
  'activity.audit.bakery.payout_verification_updated': 'Payout verification updated',
  'activity.audit.internal.connected_account_created': 'Stripe account created',
  'activity.audit.internal.connected_account_retired': 'Stripe account retired',
  'activity.audit.internal.legal_identity_updated': 'Legal identity updated by operations',
  'activity.audit.internal.payout_approved': 'Payout approved',
  'activity.audit.internal.payout_retried': 'Payout retried',
  'activity.audit.internal.monthly_payout_approved': 'Monthly payout approved',
  'activity.audit.internal.monthly_payout_retried': 'Monthly payout retried',
  'activity.audit.internal.payout_bank_details_link_sent': 'Bank-details link sent',
  'activity.audit.internal.payouts_held': 'Payouts held',
  'activity.audit.internal.payouts_released': 'Payouts released',
  'activity.audit.internal.payout_onboarding_link_revoked': 'Onboarding link revoked',
  'activity.audit.internal.payout_onboarding_link_sent': 'Onboarding link sent',
  'activity.audit.system.payout_onboarding_link_opened': 'Onboarding link opened',
  'activity.audit.system.payout_onboarding_link_sent': 'Onboarding link sent by the platform',
  'activity.audit.partner.payout_onboarding_link_created': 'Onboarding link created',
  'activity.audit.partner.payout_verification_updated': 'Payout verification updated',
  'activity.audit.partner.apartments_servable_changed': 'Apartments servability changed',
  'activity.audit.partner.properties_imported': 'Apartments imported',
  'activity.audit.partner.property_created': 'Apartment created',
  'activity.audit.partner.property_mode_assigned': 'Apartment menu type changed by the owner',
  'activity.audit.partner.property_updated': 'Apartment updated',
  'activity.audit.partner.qr_batch_generated': 'QR batch generated',
  'activity.audit.partner.settings_updated': 'Partner settings updated',
  /* These are written by the platform today and had no title, so the Activity
     feed printed the raw action - `internal.org_suspended`, which is the single
     most common audit row in the demo world. */
  'activity.audit.internal.org_suspended': 'Organization suspended',
  'activity.audit.internal.org_reinstated': 'Organization reinstated',
  'activity.audit.internal.org_served_modes_changed': 'B1/B2 selection changed',
  'activity.audit.internal.bakery_location_suspended': 'Shop suspended',
  'activity.audit.internal.bakery_location_reinstated': 'Shop reinstated',
  'activity.audit.internal.kill_switch_engaged': 'Kill switch engaged',
  'activity.audit.internal.kill_switch_disengaged': 'Kill switch released',
  /* Written by a demo repair script rather than by the platform, so no gate
     asks for this title and the feed printed the code itself at whoever
     opened Activity on the demo world. A script's row is still a row an
     operator reads. */
  'activity.audit.internal.demo_stripe_placeholder_reset': 'Demo Stripe placeholder reset',
  /* An operator's cancel of one order (OPS-002, SUP-008). */
  'activity.audit.internal.order_cancelled': 'Order canceled',
  /* Rows written by the bulk actions retired on 14 October 2026 (Internal acts
     on one record at a time). Nothing writes them any more; the titles stay
     while audit rows that carry them may still be read. */
  'activity.audit.internal.bulk_cancel': 'Orders canceled',
  'activity.audit.internal.bulk_cancel_with_refund': 'Orders canceled',
  'activity.audit.internal.bulk_delay_pickup': 'Pickups delayed in bulk',
  'activity.audit.internal.bulk_resend_notification': 'Notifications resent in bulk',
  'activity.audit.internal.delivery_dispatch_now': 'Delivery dispatched now',
  'activity.audit.internal.delivery_rebook': 'Delivery rebooked',
  'activity.audit.bakery.pairing_sent': 'Pairing code sent',
  'activity.audit.bakery.pin_sent': 'Counter PIN sent',
  /* Nine the gate found that the demo world has not written yet - every one of
     them a real producer, and every one a row that would have printed its own
     snake_case code at whoever opened the feed on the morning it first
     happened. */
  'activity.audit.bakery.pin_locked': 'Counter locked out of its PIN',
  'activity.audit.partner.location_contact_updated': 'Location contact updated',
  'activity.audit.partner.location_reactivated': 'Location reactivated',
  'activity.audit.partner.location_suspended': 'Location suspended',
  'activity.audit.partner.location_updated': 'Location updated',
  'activity.audit.bakery.order_cancelled': 'Order canceled by the counter',
  'activity.audit.bakery.pin_requested': 'PIN requested',
  'activity.audit.bakery.pin_unlocked': 'Screen unlocked',
  'activity.audit.order.guest_cancelled': 'Order canceled by the guest',
  /* The counter fleet's release channel (OPS-029). */
  'activity.audit.handheld.release_uploaded': 'Counter build uploaded',
  'activity.audit.handheld.release_signed': 'Counter build signed',
  'activity.audit.handheld.release_published': 'Counter build published',
  'activity.audit.handheld.force_set': 'Counter build forced',
  'activity.audit.handheld.force_cleared': 'Forced build removed',
  'activity.audit.handheld.release_downloaded': 'Counter build downloaded',
  'activity.audit.handheld.rollout_widened': 'Counter rollout widened',
  'activity.audit.handheld.release_halted': 'Counter rollout halted',
  'activity.audit.handheld.release_unhalted': 'Counter rollout halt cleared',

  // -- orders (OPS-002) ------------------------------------------------------
  'nav.orders': 'Orders',
  'orders.title': 'Orders',
  'orders.subtitle':
    'Every order still in flight. Filter to what you mean, tick the rows, and the action applies to exactly those.',
  'orders.scope': 'Showing',
  'orders.scope.ACTIVE': 'In flight',
  'orders.scope.NEEDS_ACTION': 'Needs action',
  'orders.scope.ALL': 'Every order',
  'orders.search': 'Search',
  'orders.filter.lane': 'Lane',
  'orders.filter.type': 'Type',
  'orders.column.order': 'Order',
  'orders.column.type': 'Type',
  'orders.type.IMMEDIATE': 'Immediate order',
  'orders.type.PRE_ORDER': 'Pre-order',
  'orders.type.MULTI_DAY': 'Multi-day pre-order',
  'orders.typeCode.IMMEDIATE': 'I',
  'orders.typeCode.PRE_ORDER': 'P',
  'orders.typeCode.MULTI_DAY': 'MP',
  'orders.column.serviceDay': 'Delivery',
  'orders.column.bakery': 'Bakery',
  'orders.column.destination': 'Destination',
  'orders.column.client': 'Client',
  'orders.column.total': 'Total',
  'orders.column.state': 'State',
  'orders.itemCount': { one: '{count} item', other: '{count} items' },
  'orders.multiDay': 'Multi-day stay',
  'orders.needsAction': 'Needs an operator',
  'orders.rowActions': 'Order actions',
  'orders.rowActionsFor': 'Actions for order {code}',
  'orders.action.delay': 'Delay pickup',
  'orders.action.reassign': 'Reassign to another bakery',
  /* Just "Cancel": it moves no money (decision 9). The refund it used to name
     is confirmed in Finance > Refunds, per order and by a person. */
  'orders.action.cancel': 'Cancel order',
  /* The cancelled order whose provider refused the job cancel (issue #1471). */
  'orders.courierNotStoodDown': 'Courier not stood down: {codes}',

  'order.actionField': 'Action',

  // -- dead letter (OPS-002) ------------------------------------------------
  'dlq.title': 'Dead-letter events',
  'dlq.subtitle':
    'Failed webhook and outbox events. Processing is idempotent, so a replay is safe to repeat.',
  'dlq.empty': 'The dead-letter queue is empty.',
  'dlq.queue': 'Queue',
  'dlq.column.kind': 'Event',
  'dlq.column.failedAt': 'Failed',
  'dlq.column.attempts': 'Attempts',
  'dlq.column.error': 'Last error',
  // "Closed", not "Replayed": a row is closed by a successful replay OR by a
  // discard, and stamping a failed replay "Replayed" contradicted the banner.
  'dlq.column.replayed': 'Closed',
  'dlq.externalId': 'External id {id}',
  'dlq.rowActions': 'Dead letter actions',
  'dlq.rowActionsFor': 'Actions for {kind}',
  'dlq.replay': 'Replay',
  'dlq.replayResult': 'Queued for retry: {replayed}, failed: {failed}.',
  'dlq.discard': 'Discard',
  'dlq.discardResult': 'Discarded {discarded}, failed {failed}. Recorded on the audit log.',
  'dlq.payload': 'Payload',

  // -- kill switch (OPS-009) ------------------------------------------------
  'kill.title': 'Kill switch',
  'kill.subtitle':
    'Stop accepting new orders at global, city, bakery or property scope. Orders already accepted are unaffected, and the guest storefront shows a service-paused screen rather than an error.',
  'kill.engaged': { one: '{count} scope engaged', other: '{count} scopes engaged' },
  'kill.none': 'No kill switch is engaged. The platform is accepting orders.',
  'kill.scope': 'Scope',
  /* How wide an engaged switch reaches: plain text in the Scope column, not a
     pill (#2004). A property is an Apartment, as everywhere else on Internal. */
  'killScope.GLOBAL': 'Global',
  'killScope.CITY': 'City',
  'killScope.BAKERY': 'Bakery',
  'killScope.PROPERTY': 'Apartment',
  'kill.target': 'Target',
  'kill.targetCity': 'City',
  'kill.targetCity.hint':
    'The city to pause, exactly as it is written on the bakery addresses, e.g. Paris.',
  'kill.column.engaged': 'Engaged',
  'kill.engagedBy': 'Engaged by {actor} at {time}',
  'kill.engage': 'Stop accepting orders',
  'kill.release': 'Resume accepting orders',
  'kill.confirm.engage.title': 'Stop accepting orders?',
  'kill.confirm.engage.body':
    'New orders in scope {scope} stop immediately. Orders already accepted are unaffected and will still be delivered.',
  'kill.confirm.release.title': 'Resume accepting orders?',
  'kill.confirm.release.body': 'Guests in scope {scope} will be able to order again immediately.',
  'kill.confirmWord': 'STOP',

  // -- organizations (OPS-003) ----------------------------------------------
  'orgs.bakeries.title': 'Bakeries',
  'orgs.bakeries.subtitle':
    'Supplier organizations. Commercial arrangements are agreed off-platform; this portal records the resulting configuration.',
  'orgs.properties.title': 'Clients',
  'orgs.properties.subtitle':
    'Conciergeries and independent owners. Commercial arrangements are agreed off-platform; this portal records the resulting configuration.',
  'orgs.search': 'Name contains',
  'orgs.type': 'Type',
  'orgs.type.CONCIERGERIE': 'Conciergerie',
  'orgs.type.INDEPENDENT_OWNER': 'Independent owner',
  'orgs.type.BAKERY': 'Bakery',
  'orgs.type.INTERNAL': 'Internal',
  'orgs.column.name': 'Name',
  'orgs.column.payout': 'Payout',
  'orgs.column.qr': 'QR codes',
  'orgs.column.mode': 'Default mode',
  'orgs.column.state': 'State',
  'orgs.column.apartments': 'Apartments',
  // Servable first: the question a reader has is how much of this portfolio works.
  'orgs.apartments': '{servable} of {listed} servable',
  'orgs.rowActions': 'Organization actions',
  'orgs.rowActionsFor': 'Actions for {name}',
  'orgs.add': 'Add an organization',
  'orgs.add.bakery.title': 'Add a bakery',
  'orgs.add.property.title': 'Add an organization',
  'orgs.reference': 'Reference',
  'orgs.field.code': 'Organization code',
  'orgs.code.hint': 'Names its invoices, payouts and cycles',
  'orgs.code.invalid': 'Four letters and two digits',
  'orgs.rename': 'Rename',
  'orgs.rename.title': 'Rename {name}',
  'orgs.info': 'View info',
  'orgs.info.title': 'Information',
  'orgs.info.name': 'Display name',
  'orgs.info.stripeAccount': 'Stripe account',
  'orgs.info.commissionRate': 'Commission rate',
  'orgs.info.minimumBasket': 'Minimum basket',
  'orgs.info.locations': 'Locations',
  'orgs.info.created': 'Created',
  'orgs.action.propertyMode': 'Put every apartment on mode {mode}',
  'orgs.assign.title': 'Assign mode {mode} to {name}?',
  'orgs.assign.confirm': 'Assign mode {mode}',
  'orgs.assign.propertyBody':
    'Mode {mode} becomes the only menu type {name} can use, and every apartment moves to it. A guest sees the change on their next scan; the change is audited against your name.',
  'orgs.qrUsage': '{used} of {cap}',
  'orgs.active': 'Active',
  'orgs.suspended': 'Suspended',
  'bakeries.pickupMissing.CONTACT_MOBILE': 'No contact mobile',
  'bakeries.pickupMissing.ADDRESS': 'No address',
  'bakeries.pickupMissing.COORDINATES': 'No coordinates',
  'orgs.suspendedFor.MANUAL': 'Suspended by an operator',
  'orgs.suspendedFor.ONBOARDING': 'Stripe onboarding unfinished',
  'orgs.suspendedFor.VAT_STATUS': 'VAT status not stated',
  // A row that has been asked to change and is waiting for the answer. It
  // shows what was asked for, marked - never asserted as done.
  'pending.suffix': '(saving)',
  // A payout sent again as it was frozen, beside its state (#2004, P108): text, not a pill.
  'payout.retry': 'Retry after a failed payout',
  'pending.dismiss': 'Dismiss',
  'bakeries.pendingFailed': '{name} was not changed: {reason}',
  'orgs.suspend': 'Suspend',
  'orgs.unsuspend': 'Lift suspension',
  'orgs.suspend.confirm.title': 'Suspend this organization?',
  'orgs.suspend.confirm.body':
    'New orders from {name}’s QR codes stop and pending payouts are held. The action is audited against your name.',
  'orgs.unsuspend.confirm.title': 'Lift the suspension?',
  'orgs.unsuspend.confirm.body':
    'Orders from {name}’s QR codes resume and held payouts are released.',
  'orgs.edit': 'Edit configuration',
  'orgs.edit.title': 'Configuration',
  'orgs.edit.name': 'Display name',
  'orgs.edit.qrCap': 'QR generation cap',
  /* The unit is in the label because the field takes basis points, as the rates
     screen's does - the percentage under the box is the same number read back,
     and a box that took "5" for five per cent on one screen and 500 on another
     is how a commission gets set a hundred times too low. */
  'orgs.edit.commissionRate': 'Commission rate (basis points)',
  /* Under the ONE ticked menu type, and only while the change is unsaved: the
     save moves apartments the owner may have placed themselves, so the row
     says so before the button is pressed (OPS-013). */
  'orgs.edit.modes.reset': 'Saving moves every apartment to this mode.',
  'orgs.edit.save': 'Save configuration',

  // -- one client's own record (OPS-003) ------------------------------------
  'clients.apartments.title': 'Apartments',
  'clients.apartments.downloadAllQr': 'Download all QR codes',
  'clients.apartments.downloadAllQr.preparing': 'Preparing',
  'clients.apartment.column.name': 'Apartment',
  'action.menu.viewQr': 'View QR code',
  'action.menu.openGuestView': 'Open the guest ordering view',

  // -- bakeries: three tabs and the detail screen (OPS-003, OPS-013) --------
  // The three tab labels are the three screens' own titles, so the strip and
  // the heading under it cannot disagree.
  'bakeries.modeAHouses.title': 'Mode A houses',
  'bakeries.modeAShops.title': 'Mode A shops',
  'bakeries.shops.title': 'Mode B shops',
  'bakeries.houses.title': 'Mode B houses',
  'bakeries.search': 'Name or address contains',
  'bakeries.column.address': 'Address',
  'bakeries.column.house': 'House',
  'bakeries.column.locations': 'Locations',
  /* The Mode B houses table: one tick box per apartment mode, one switch per house. */
  'bakeries.serves': '{name} serves {mode}',
  'bakeries.activeFor': '{name} active',
  // The shop is dark because its house is, which its own state cannot say.
  'bakeries.activate': 'Activate',
  'bakeries.suspend': 'Suspend',
  'bakeries.delete': 'Delete permanently',
  'bakeries.delete.hasOrders': 'Delete permanently - orders in progress, suspend instead',
  'bakeries.delete.title': 'Delete {name} permanently?',
  'bakeries.delete.body': '{name} is removed for good. The action is audited against your name.',
  'bakeries.delete.blocked': {
    one: '{name} still has {count} order in progress. Delete it once it is done.',
    other: '{name} still has {count} orders in progress. Delete them, then delete it.',
  },
  'bakeries.suspend.confirm.title': 'Suspend this shop?',
  'bakeries.suspend.confirm.body':
    '{name} wins no allocation while it is suspended. The action is audited against your name.',
  'bakeries.activate.confirm.title': 'Reinstate this shop?',
  'bakeries.activate.confirm.body': '{name} can win allocation again.',
  'bakeries.add.modeA': 'Add a bakery',
  'bakeries.add.shop': 'Add a shop',
  'bakeries.add.house': 'Add a house',
  'bakeries.field.house': 'House',
  'bakeries.field.name': 'Name',
  'bakeries.field.houseName': 'House name',
  'bakeries.field.locationName': 'Location name',
  'bakeries.field.addressLine1': 'Address',
  'bakeries.field.addressLine2': 'Address line 2',
  'bakeries.field.postalCode': 'Postal code',
  'bakeries.field.city': 'City',
  'bakeries.column.devices': 'Devices',
  'bakeries.devices.none': 'No device',
  'bakeries.filter.device': 'Device',
  'bakeries.filter.device.PAIRED': 'Paired',
  'bakeries.filter.device.UNPAIRED': 'No device',
  'bakeries.devices.count': { one: '{count} device', other: '{count} devices' },
  'bakeries.field.contactPerson': 'Contact',
  'bakeries.field.contactEmail': 'Email',
  'bakeries.field.contactMobile': 'Mobile',
  /* The first tab of a house record - its own facts, as against its prices. */
  'bakeries.detail.record': 'House',
  'bakeries.detail.identity': 'Identity',
  'bakeries.detail.hours': 'Pickup windows',
  'bakeries.detail.service': 'Service',
  'bakeries.detail.closures': 'Closures',
  /*
   * Shutting a day that already carries orders (BAK-047 clause 5, amended
   * 2026-10-09). One total and never a split: every order the closure takes
   * leaves the shop the way a rejected one does, and the platform hands it on
   * to another house, or ends it, on its next pass.
   */
  'bakeries.closeDay.title': 'Close this day?',
  'bakeries.closeDay.affectedOne': '1 order affected.',
  'bakeries.closeDay.affectedMany': '{count} orders affected.',
  'bakeries.closeDay.confirm': 'Close the day',
  'bakeries.detail.brand': 'Brand',
  'bakeries.detail.commercial': 'Commercial',
  'bakeries.field.identifier': 'Identifier',
  'bakeries.field.coordinates': 'Coordinates',
  // The seven weekday spans (BAK-027). Monday first, ISO order.
  'bakeries.weekday.1': 'Monday',
  'bakeries.weekday.2': 'Tuesday',
  'bakeries.weekday.3': 'Wednesday',
  'bakeries.weekday.4': 'Thursday',
  'bakeries.weekday.5': 'Friday',
  'bakeries.weekday.6': 'Saturday',
  'bakeries.weekday.7': 'Sunday',
  'bakeries.field.closed': 'Closed',
  'bakeries.field.earliestPickup': 'Earliest pickup',
  'bakeries.field.latestPickup': 'Latest pickup',
  'bakeries.field.hourLabel': 'Hour',
  'bakeries.field.minuteLabel': 'Minute',
  'bakeries.field.serviceMorning': 'Morning',
  'bakeries.field.serviceAfternoon': 'Afternoon',
  'bakeries.field.addAfternoon': 'Add an afternoon',
  'bakeries.field.removeService': 'Remove',
  'bakeries.field.servicesOverlap': 'The afternoon starts before the morning ends.',
  'bakeries.field.pickupSpanInvalid': 'The end must be after the start.',
  // Administered platform-wide (BAK-003, BAK-004), shown read-only here and on
  // the baker's own screen.
  'bakeries.field.immediateAnswer': 'Immediate answer window',
  'bakeries.field.immediatePrep': 'Immediate preparation time',
  'bakeries.field.serviceRadius': 'Service radius in meters',
  'bakeries.field.maxPickupsPerHour': 'Maximum pickups per hour',
  'bakeries.field.bagPhotos': 'Bag photos',
  'bakeries.field.bagPhotosOff': 'Off',
  'bakeries.field.bagPhotosOptional': 'Optional',
  'bakeries.field.bagPhotosMandatory': 'Mandatory',
  'bakeries.field.brandDisplayName': 'Brand display name',
  'bakeries.field.brandDescription': 'Brand description',
  'bakeries.field.brandLogo': 'Logo',
  'bakeries.field.brandImagery': 'Imagery',
  'bakeries.legal.title': 'Legal identity',
  'bakeries.legal.legalName': 'Registered name',
  'bakeries.legal.legalForm': 'Legal form',
  'bakeries.legal.siren': 'SIREN',
  'bakeries.legal.siret': 'SIRET',
  'bakeries.legal.vatNumber': 'Intra-Community VAT number',
  'bakeries.legal.vatStatus': 'VAT status',
  'bakeries.legal.vatStatusUnstated': 'Not stated',
  'bakeries.legal.vatStatus.REGISTERED': 'VAT registered',
  'bakeries.legal.vatStatus.FRANCHISE_EN_BASE': 'Franchise en base',
  'bakeries.legal.vatStatus.PRIVATE_INDIVIDUAL': 'Private individual',
  'bakeries.legal.registeredOffice': 'Registered office',
  'bakeries.legal.shareCapital': 'Share capital',
  'bakeries.legal.rcsCity': 'RCS city',
  'bakeries.legal.apeCode': 'APE code',
  'bakeries.legal.contactEmail': 'Contact email',
  'bakeries.legal.invalid.legalName': 'Up to 200 characters',
  'bakeries.legal.invalid.legalForm': 'Up to 120 characters',
  'bakeries.legal.invalid.siren': '9 digits',
  'bakeries.legal.invalid.siret': '14 digits',
  'bakeries.legal.invalid.vatNumber': 'Country code, then up to 12 letters or digits',
  'bakeries.legal.invalid.vatStatus': 'Pick one',
  'bakeries.legal.invalid.registeredOffice': 'Up to 6 lines of 120 characters',
  'bakeries.legal.invalid.shareCapital': 'Up to 60 characters',
  'bakeries.legal.invalid.rcsCity': 'Up to 80 characters',
  'bakeries.legal.invalid.apeCode': '4 digits and a letter',
  'bakeries.legal.invalid.contactEmail': 'Not an email address',
  'bakeries.payouts.title': 'Payouts',
  'bakeries.payouts.status': 'Status',
  'bakeries.payouts.account': 'Stripe account',
  'bakeries.payouts.link': 'Link',
  'bakeries.payouts.link.none': 'Not sent',
  'bakeries.payouts.link.sent': 'Sent {at} to {to}, by {by}',
  'bakeries.payouts.link.LIVE': 'Live until {until}, opened {count}×',
  'bakeries.payouts.link.LIVE.unopened': 'Live until {until}, not opened yet',
  'bakeries.payouts.link.EXPIRED': 'Expired',
  'bakeries.payouts.link.REVOKED': 'Revoked',
  'bakeries.payouts.link.COMPLETED': 'Used',
  'bakeries.payouts.waiting': 'Stripe is waiting for',
  'bakeries.payouts.blocked': 'Blocked: {reason}',
  'bakeries.payouts.send': 'Send onboarding link',
  'bakeries.payouts.send.confirm.title': 'Create the Stripe account',
  'bakeries.payouts.send.confirm': 'This creates the organization’s Stripe account. Continue?',
  'bakeries.payouts.revoke': 'Revoke link',
  'bakeries.payouts.missing': 'Missing: {fields}',
  'bakeries.payouts.missing.legalName': 'legal name',
  'bakeries.payouts.missing.legalForm': 'legal form',
  'bakeries.payouts.missing.siren': 'SIREN',
  'bakeries.payouts.missing.registeredOffice': 'registered office',
  'bakeries.payouts.missing.contactEmail': 'contact email',
  'bakeries.payouts.missing.taxResidence': 'tax residence',
  'bakeries.payouts.missing.bankCountry': 'bank country',
  // A partner's legal identity, with the owner's own labels (partner-app legalIdentity.*):
  // stated by the partner, its tax status and countries corrected here (owner-onboarding.html).
  'partners.legal.title': 'Legal identity',
  'partners.legal.taxKind': 'Tax status',
  'partners.legal.taxKind.INDIVIDUAL': 'Individual',
  'partners.legal.taxKind.SOLE_PROPRIETOR': 'Sole proprietorship',
  'partners.legal.taxKind.COMPANY': 'Company',
  'partners.legal.unstated': 'Not stated',
  'partners.legal.legalName': 'Registered name',
  'partners.legal.personName': 'Full name',
  'partners.legal.legalForm': 'Legal form',
  'partners.legal.siren': 'SIREN',
  'partners.legal.registeredOffice': 'Registered office',
  'partners.legal.vatNumber': 'Intra-Community VAT number',
  'partners.legal.vatStatus': 'VAT status',
  'partners.legal.vatStatus.REGISTERED': 'VAT registered',
  'partners.legal.vatStatus.FRANCHISE_EN_BASE': 'Franchise en base',
  'partners.legal.vatStatus.PRIVATE_INDIVIDUAL': 'Private individual',
  'partners.legal.taxResidence': 'Country of tax residence',
  'partners.legal.bankCountry': 'Bank country',
  'partners.legal.locked': 'Verified by Stripe. New details need a new account.',
  'bakeries.save': 'Save',
  'bakeries.invalid': 'Not a value this field accepts.',
  // Closures (BAK-010): the date ranges a shop is shut.
  'bakeries.closures.closedWeekly': 'Closed every week',
  'bakeries.closures.previousMonth': 'Previous month',
  'bakeries.closures.nextMonth': 'Next month',
  'month.1': 'January',
  'month.2': 'February',
  'month.3': 'March',
  'month.4': 'April',
  'month.5': 'May',
  'month.6': 'June',
  'month.7': 'July',
  'month.8': 'August',
  'month.9': 'September',
  'month.10': 'October',
  'month.11': 'November',
  'month.12': 'December',

  // -- pairing a counter device (device-pairing plan 5, OPS-024, BAK-033..037)
  // Every one of these is on the SHOP row's menu. The three actions are kept
  // apart on purpose: Send re-sends, Regenerate retires the old QR, and Remove
  // - inside Devices - is the only thing that kills a token.
  'pairing.menu.show': 'Show pairing code',
  'pairing.menu.sendCode.email': 'Send pairing code by email',
  'pairing.menu.sendCode.sms': 'Send pairing code by SMS',
  'pairing.menu.regenerate': 'Regenerate pairing code',
  'pairing.menu.sendPin.email': 'Send PIN by email',
  'pairing.menu.sendPin.sms': 'Send PIN by SMS',
  'pairing.menu.resetPin': 'Reset PIN',
  'pairing.menu.setPin': 'Set PIN',
  'pairing.menu.unlock': 'Unlock now',
  'pairing.menu.notLocked': 'This counter is not locked',
  'pairing.menu.devices': 'Devices',
  'pairing.code.title': 'Pairing code',
  'pairing.code.issued': 'Issued {date}',
  'pairing.print.notice': 'Do not display in the shop',
  'pairing.action.copyLink': 'Copy link',
  'pairing.action.copied': 'Link copied',
  'pairing.action.copyFailed': 'The link was not copied.',
  'pairing.action.print': 'Print',
  'pairing.action.sendEmail': 'Send by email',
  'pairing.action.sendSms': 'Send by SMS',
  'pairing.send.title.code': 'Send the pairing code',
  'pairing.send.title.pin': 'Send the PIN',
  'pairing.send.confirm': 'Send',
  'pairing.send.noPin': 'No PIN set yet.',
  'pairing.field.email': 'Email',
  'pairing.field.mobile': 'Mobile',
  'pairing.sent': 'Sent to {destination}',
  /* What became of the last code or PIN this shop was sent (TST-002). "Held" is
     the demo instance deliberately not sending, and it is neither of the other
     two - it read as a delivery until the ledger could tell them apart. */
  'pairing.lastSend.line': 'Last send: {channel}, {at}, {outcome}',
  'pairing.lastSend.sms': 'SMS',
  'pairing.lastSend.email': 'Email',
  'pairing.lastSend.delivered': 'delivered',
  'pairing.lastSend.held': 'held ({reason})',
  'pairing.lastSend.failed': 'failed ({reason})',
  'pairing.regenerate.title': 'Regenerate the pairing code?',
  'pairing.regenerate.body': 'Paired devices keep working. The old QR code stops pairing.',
  'pairing.regenerate.confirm': 'Regenerate',
  'pairing.regenerate.done': 'New pairing code',
  'pairing.resetPin.title': 'Reset the PIN?',
  'pairing.resetPin.body':
    'The counter is asked for a new code at its next lock after service. Pairing is paused until then.',
  'pairing.resetPin.confirm': 'Reset PIN',
  'pairing.resetPin.done': 'PIN reset pending',
  'pairing.setPin.title': 'Set the PIN',
  'pairing.setPin.pin': 'PIN',
  'pairing.setPin.weak': 'That code is too simple. Choose another one.',
  'pairing.setPin.send': 'Send to',
  'pairing.setPin.dontSend': 'Do not send',
  'pairing.setPin.confirm': 'Set PIN',
  'pairing.setPin.done': 'PIN set',
  'pairing.setPin.doneSent': 'PIN set and sent to {destination}',
  'pairing.unlock.title': 'Unlock this counter?',
  'pairing.unlock.notLocked': 'This counter is not locked.',
  'pairing.unlock.confirm': 'Unlock now',
  'pairing.unlock.done': 'Unlocked',
  'pairing.lockout.locked': {
    one: 'Locked until {until} - {count} failed attempt',
    other: 'Locked until {until} - {count} failed attempts',
  },
  'pairing.lockout.failures': {
    one: '{count} failed attempt',
    other: '{count} failed attempts',
  },
  'pairing.pin.NOT_SET': 'No PIN set',
  'pairing.pin.SET': 'PIN set on {date}',
  'pairing.pin.SET.by': 'PIN set on {date} by {by}',
  'pairing.pin.SET.undated': 'PIN set',
  'pairing.pin.RESET_PENDING': 'A new PIN is pending at the counter',
  'pairing.devices.title': 'Devices',
  'pairing.devices.column.pairedAt': 'Paired on',
  'pairing.devices.column.lastSeen': 'Last seen',
  'pairing.devices.kind.SUNMI': 'Sunmi',
  'pairing.devices.kind.BROWSER': 'Browser',
  // A row adopted from a token issued before pairing existed; the next
  // heartbeat fills it in.
  'pairing.devices.kind.unknown': 'Unknown',
  'pairing.devices.never': 'Never',
  'pairing.devices.empty': 'No device is paired with this shop.',
  'pairing.devices.removed.done': 'Device removed',
  'pairing.devices.remove': 'Remove',
  'pairing.devices.remove.title': 'Remove {name}?',
  'pairing.devices.remove.body':
    'It leaves this shop. A handheld stays in the fleet and keeps updating.',
  'pairing.devices.remove.inService': 'In service now.',

  // -- storefront modes (OPS-013) -------------------------------------------
  //
  // TWO VOCABULARIES, because there are two different things called a mode and
  // splitting mode B split only one of them (founder, 2026-09-20).
  //
  // An APARTMENT has a menu type, and there are three: A, B1 and B2. That is
  // what a client's ticked boxes, an apartment row, an order and the dashboard
  // are about - `modes.mode.*` for the sold types, `modes.tag.*` for the short
  // word printed on a row.
  //
  // A BAKERY has a POOL, and there are still two: it sells into the curated
  // pool or it sells under its own name. B1 and B2 apartments both buy from
  // that mode B pool - each from the houses ticked for it on the Mode B houses
  // table, whose column headings are `modes.name.*` - so a house is never
  // "mode B1" and the coverage map has two filters and not three - `modes.pool.*`.
  'modes.mode': 'Mode',
  'modes.mode.A': 'Mode A - unbranded curated pool',
  'modes.mode.B': 'Mode B1 - presented by name',
  'modes.mode.B2': 'Mode B2 - closest shop, unnamed',
  // The short tag on an apartment row, an order and the dashboard. Ordinary
  // text, never a pill: it is two words and a pill holds one.
  'modes.tag.A': 'Mode A',
  'modes.tag.B': 'Mode B1',
  'modes.tag.B2': 'Mode B2',
  // The mode's NAME alone, for a sentence that already says "mode". The stored
  // value `B` is today's mode B1, so a sentence must never print the value.
  'modes.name.A': 'A',
  'modes.name.B': 'B1',
  'modes.name.B2': 'B2',
  // A bakery's pool, on the coverage map's filter and anywhere else a HOUSE's
  // mode is named.
  'modes.pool.A': 'Mode A',
  'modes.pool.B': 'Mode B',

  // -- rates (OPS-015) ------------------------------------------------------
  'rates.title': 'Commission',
  'rates.subtitle':
    'Each client carries its own commission, versioned with an effective date. A change never alters orders already placed.',
  'rates.default': 'Default',
  'rates.filter.type': 'Type',
  'rates.column.client': 'Clients',
  'rates.column.type': 'Type',
  'rates.column.changedAt': 'Changed at',
  'rates.column.effectiveFrom': 'Effective from',
  'rates.column.version': 'Version',
  'rates.column.setBy': 'Set by',
  'rates.figure.commissionBps': 'Commission',
  'rates.history.title': 'Every version',
  // PAY-023: a fee charged only under a basket, and off until somebody sets one.
  'rates.belowThreshold': 'below',
  'rates.off': 'Off',
  // What the delivery pass-through is applied to (PAY-019, PAY-020).
  'rates.help.DELIVERY_PASS_THROUGH':
    'Applied to the courier’s tax-inclusive price; at 100% the guest pays exactly what the courier costs.',
  'rates.form.bpsUnit': '(basis points)',
  'rates.form.euroUnit': '(euros)',
  'rates.form.basisPointsHint': '10000 basis points is 100%.',
  // PAY-023: the basket as the guest reads it, VAT included (founder, 2026-09-21),
  // like the fee box beside it - every amount on this screen is what the guest sees.
  'rates.form.threshold': 'Charged below (euros, incl. VAT)',
  'rates.form.pairRequired': 'A fee and the basket it applies below are set together.',
  'rates.form.save': 'Save the rate',
  'rates.confirm.body':
    'Applies 5 minutes after the last change. Orders already placed keep what they were placed under.',

  // -- financial parameters (OPS-015) ---------------------------------------
  'documents.title': 'Documents',
  'documents.customer.title': 'Customer invoices',
  'documents.supplier.title': 'Supplier invoices',
  'orgs.tabs.information': 'Information',
  'orgs.tabs.payouts': 'Payout cycles',
  'orgs.payouts.statement': 'Detailed payout statement',
  'orgs.payouts.documents': 'Documents',
  'documents.search': 'Search documents',
  'documents.empty': 'No documents',
  'documents.column.number': 'Number',
  'documents.column.statement': 'Statement',
  'documents.column.family': 'Document',
  'documents.column.party': 'Party',
  'documents.column.date': 'Date',
  'documents.column.total': 'Total incl. VAT',
  'documents.column.state': 'State',
  'documents.column.accounting': 'Pennylane',
  'documents.filter.month': 'Month',
  'documents.filter.organization': 'Organization',
  'documents.family.GUEST_FEES': 'Guest fees',
  'documents.family.GUEST_PRODUCTS': 'Guest products',
  'documents.family.BAKERY_COMMISSION': 'Baker commission',
  'documents.family.PARTNER_SELF_BILL': 'Self-bill',
  'documents.family.PARTNER_STATEMENT': 'Private owner statement',
  'documents.nature.INVOICE': 'Invoice',
  'documents.nature.CREDIT_NOTE': 'Credit note',
  'documents.nature.STATEMENT': 'Statement',
  'documents.rowActions': 'Actions',
  'documents.rowActionsFor': 'Actions for {number}',
  'documents.action.download': 'Download',
  'documents.action.openPayout': 'Open payout',
  'documents.action.openOrder': 'Open order',
  'documents.action.retry': 'Retry export',
  'documents.action.reissue': 'Cancel and reissue',
  'documents.reissue.title': 'Cancel and reissue',
  'documents.reissue.body':
    '{number} is canceled by a credit note and issued again with the organization’s current legal identity.',
  'documents.financeOnly': 'Finance approvers only',
  'documents.retried': 'Export queued',
  'documents.reissued': 'Document reissued',
  'documents.downloadFailed': 'The document could not be downloaded',
  'parameters.accounting.title': 'Pennylane',
  'parameters.accounting.guestCustomer': 'Guest customer ID',
  'parameters.accounting.ledger.DELIVERY': 'Delivery fee ledger account',
  'parameters.accounting.ledger.SERVICE': 'Service fee ledger account',
  'parameters.accounting.ledger.SMALL_ORDER': 'Small-order fee ledger account',
  'parameters.accounting.ledger.COMMISSION': 'Commission ledger account',
  'parameters.accounting.save': 'Save',
  'parameters.accounting.invalid': 'Enter a positive whole number, or leave it empty',
  'parameters.title': 'Financial parameters',
  'parameters.subtitle':
    'Common to every client, each versioned with an effective date. A change never alters orders already placed.',
  'parameters.column.parameter': 'Financial parameter',
  'parameters.column.value': 'Value',
  'parameters.name.SERVICE_FEE': 'Service fee',
  'parameters.name.SMALL_ORDER_FEE': 'Small-order fee',
  'parameters.name.DELIVERY_SETUP': '{provider} delivery setup',
  'parameters.disabled': 'Disabled',
  'parameters.percentage': '{rate} of the courier price',
  'parameters.tier.range': '{from} to < {to}: {price}',
  'parameters.tier.open': '>= {from}: {price}',
  'parameters.tier.upTo': 'to < {to}',
  'parameters.tier.above': 'and above',
  'parameters.pendingFrom': 'from {at}',
  'parameters.form.enabled': 'Active',
  'parameters.form.enabledHint': 'When off, the guest pays exactly what the courier costs.',
  'parameters.form.tiers': 'Delivery price by basket',
  'parameters.form.tierFrom': 'Basket from (euros)',
  'parameters.form.tierKind': 'Charged as',
  'parameters.charge.PERCENTAGE': '% of delivery cost',
  'parameters.charge.FIXED': 'Fixed price (euros)',
  'parameters.form.tierPrice': 'Delivery price',
  'parameters.form.addTier': 'Add a line',
  'parameters.form.removeTier': 'Remove',
  'parameters.form.tiersInvalid': 'Each line starts above the one before.',
  'parameters.form.percentUnit': '(%)',
  // Every amount of the platform's own fees is what the guest reads (PAY-018, PAY-023).
  'parameters.form.vatIncludedEuroUnit': '(euros, incl. VAT)',
  'parameters.form.useMinimum': 'Use a minimum',
  'parameters.form.useMaximum': 'Use a maximum',
  'parameters.form.minimum': 'Minimum',
  'parameters.form.maximum': 'Maximum',
  'parameters.form.boundsCrossed': 'The minimum is above the maximum.',
  'parameters.serviceFee.minimum': 'Minimum {amount}',
  'parameters.serviceFee.maximum': 'Maximum {amount}',
  'parameters.form.save': 'Save',

  // -- finance (OPS-006, PAY-008) -------------------------------------------
  // Names the screen rather than the section: Finance is the sidebar entry and
  // owns two sub-tabs, of which this is one.
  'finance.title': 'Ledger and reconciliation',
  'finance.pending.title': 'Pending payouts',
  'finance.orders.title': 'Orders',
  'finance.orders.search': 'Search orders',
  'finance.orders.empty': 'No delivered order in this period.',
  'finance.orders.column.order': 'Order',
  'finance.orders.column.day': 'Day',
  'finance.orders.column.partner': 'Partner and rate',
  'finance.orders.column.guestTotal': 'Guest price',
  'finance.orders.column.bakeryPrice': 'Baker price',
  'finance.orders.column.commission': 'Commission',
  'finance.orders.column.partnerCommission': 'Partner commission',
  'finance.orders.column.stripeFee': 'Stripe fee',
  'finance.orders.column.deliveryFee': 'Delivery fee',
  'finance.orders.column.courierCost': 'Courier cost',
  'finance.orders.column.serviceFee': 'Service fee',
  'finance.orders.column.netMargin': 'Net margin',
  'finance.orders.refundCost': 'Refund cost',
  'finance.orders.column.bakeryPayout': 'Baker payout',
  'finance.orders.column.partnerPayout': 'Partner payout',
  'finance.payable.search': 'Search organizations',
  'finance.payable.waiting': '{count} waiting for their cycle to close',
  'finance.payable.unreadable': '{count} not read from Stripe',
  'finance.payable.balancesRead': 'Balances read {at}',
  'finance.payable.availableOn': 'Available {day}',
  'finance.payable.column.organization': 'Organization',
  'finance.payable.column.cycle': 'Cycle',
  'finance.payable.column.type': 'Type',
  'finance.payable.column.cadence': 'Cadence',
  'finance.payable.column.lastPayout': 'Last payout',
  'finance.payable.column.available': 'Available',
  'finance.payable.column.pending': 'Pending',
  'finance.payable.column.movements': 'Movements',
  'finance.payable.column.state': 'State',
  'finance.payable.type.BAKERY': 'Baker',
  'finance.payable.type.CONCIERGERIE': 'Conciergerie',
  'finance.payable.type.INDEPENDENT_OWNER': 'Independent owner',
  'finance.payable.missing': 'Missing: {fields}',
  'finance.payable.field.legalName': 'legal name',
  'finance.payable.field.legalForm': 'legal form',
  'finance.payable.field.siren': 'SIREN',
  'finance.payable.field.siret': 'SIRET',
  'finance.payable.field.vatNumber': 'VAT number',
  'finance.payable.field.vatStatus': 'VAT status',
  'finance.payable.field.registeredOffice': 'registered office',
  'finance.payable.action.preview': 'Preview & approve',
  'finance.payable.action.resend': 'Resend bank details link',
  'finance.payable.action.hold': 'Hold',
  'finance.payable.action.release': 'Release',
  'finance.payable.resent': 'Bank details link sent',
  'finance.payout.title': 'Payout',
  'finance.payout.frozen': 'Payout',
  'finance.payout.amount': 'Amount',
  'finance.payout.readAt': 'Read at',
  'finance.payout.expiresAt': 'Expires',
  'finance.payout.approve': 'Approve',
  'finance.payout.refresh': 'Refresh',
  'finance.payout.approved': 'Payout of {amount} approved',
  'finance.payout.movements': 'Movements',
  'finance.payout.history': 'Payout history',
  'finance.payout.documents': 'Documents',
  'finance.payout.noDocuments': 'No document to issue',
  'finance.payout.view': 'View',
  'finance.payout.viewFailed': 'Could not open the document',
  'finance.payout.column.document': 'Document',
  'finance.payout.column.excludingTax': 'Excl. VAT',
  'finance.payout.column.tax': 'VAT',
  'finance.payout.column.includingTax': 'Total',
  'finance.payout.column.lines': 'Lines',
  'finance.payout.column.corrects': 'Corrects',
  'finance.payout.family.GUEST_FEES': 'Guest fees',
  'finance.payout.family.GUEST_PRODUCTS': 'Guest products',
  'finance.payout.family.BAKERY_COMMISSION': 'Commission',
  'finance.payout.family.PARTNER_SELF_BILL': 'Self-bill',
  'finance.payout.family.PARTNER_STATEMENT': 'Statement',
  'finance.payout.nature.INVOICE': 'Invoice',
  'finance.payout.nature.CREDIT_NOTE': 'Credit note',
  'finance.payout.nature.STATEMENT': 'Statement',
  'finance.payout.noMovements': 'No movements',
  'finance.payout.noHistory': 'No payouts yet',
  'finance.payout.column.party': 'Party',
  'finance.payout.column.kind': 'Kind',
  'finance.payout.column.amount': 'Amount',
  'finance.payout.column.date': 'Date',
  'finance.payout.column.approvedAt': 'Approved',
  'finance.payout.column.approvedBy': 'Approved by',
  'finance.payout.party.BAKERY': 'Baker',
  'finance.payout.party.PARTNER': 'Commission',
  'finance.payout.party.BAKERY_CHARGE': 'House charge',
  'finance.payout.kind.TRANSFER': 'Transfer',
  'finance.payout.kind.REVERSAL': 'Reversal',
  'finance.pending.empty': 'No payouts are waiting for approval.',
  'finance.monthly.title': 'Croissant',
  'finance.monthly.statement': 'Monthly statement',
  'finance.monthly.revenue': 'Revenue (4b)',
  'finance.monthly.delivery': 'Delivery (4c)',
  'finance.monthly.total': 'Payout',
  'finance.monthly.orders': 'Orders',
  'finance.monthly.column.order': 'Order',
  'finance.monthly.empty': 'No month to pay out.',
  'finance.monthly.available': 'Available',
  'finance.monthly.availableOn': 'Available {day}',
  'finance.monthly.term.COMMISSION': 'Commission',
  'finance.monthly.term.MERCHANDISE_SHORTFALL': 'Products below house price',
  'finance.monthly.term.FEES': 'Service fees',
  'finance.monthly.term.PARTNER_COMMISSION': 'Partner commission',
  'finance.monthly.term.STRIPE_FEES': 'Stripe fees',
  'finance.monthly.term.ADJUSTMENTS': 'Adjustments',
  'finance.monthly.term.DELIVERY_FEES': 'Delivery fees',
  'finance.monthly.term.TIPS': 'Tips',
  'finance.monthly.term.DELIVERY_ADJUSTMENTS': 'Delivery adjustments',
  // Why the SERVER would refuse this payout, in its own words. The three codes
  // are the endpoint's, not the screen's.
  'finance.pending.rowActions': 'Payout actions',
  'finance.pending.rowActionsFor': 'Actions for {name}',
  'finance.pending.cadence.DAILY': 'Daily',
  'finance.pending.cadence.WEEKLY': 'Weekly',
  'finance.pending.cadence.MONTHLY': 'Monthly',

  'finance.pending.detail.back': 'All pending payouts',
  /* Delivery statements: one per provider cycle, matched against the
     provider's invoice (docs/finance/reconciliation.html#delivery-statement). */
  'finance.delivery.title': 'Delivery statements',
  'finance.delivery.back': 'All delivery statements',
  'finance.delivery.search': 'Search statements',
  'finance.delivery.empty': 'No delivery statements yet.',
  'finance.delivery.column.cycle': 'Cycle',
  'finance.delivery.column.provider': 'Provider',
  'finance.delivery.column.orders': 'Orders',
  'finance.delivery.column.charged': 'Charged',
  'finance.delivery.column.tips': 'Tips',
  'finance.delivery.column.state': 'State',
  'finance.delivery.column.order': 'Order',
  'finance.delivery.column.job': 'Job',
  'finance.delivery.liable': '{house} liable',
  'finance.delivery.column.day': 'Day',
  'finance.delivery.column.quoted': 'Quoted',
  'finance.delivery.column.tip': 'Tip',
  'finance.delivery.provider.stuart': 'Stuart',
  'finance.delivery.provider.uber-direct': 'Uber Direct',
  'finance.delivery.provider.mock': 'Simulator',
  'finance.delivery.statement': 'Statement',
  'finance.delivery.period': 'Period',
  'finance.delivery.quoted': 'Quoted',
  'finance.delivery.charged': 'Charged',
  'finance.delivery.tips': 'Tips',
  'finance.delivery.credits': 'Claim credits',
  'finance.delivery.creditAmount': 'Credit',
  'finance.delivery.unknownCredits': 'Credit unknown',
  'finance.delivery.expected': 'Expected',
  'finance.delivery.unknownFees': 'Fee unknown',
  'finance.delivery.invoice': 'Invoice',
  'finance.delivery.invoiceNumber': 'Invoice number',
  'finance.delivery.invoiceTotal': 'Invoice total',
  'finance.delivery.match': 'Match',
  'finance.delivery.difference': 'Difference',
  'finance.delivery.enteredBy': 'Entered by',
  'finance.delivery.jobs': 'Jobs',
  'activity.audit.finance.delivery_statement_matched': 'Delivery invoice matched',
  'activity.audit.finance.delivery_statement_differs': 'Delivery invoice differs',
  'finance.entry.PAYABLE': 'Payable',
  'finance.entry.REFUND_RECOVERY': 'Refund recovery',
  'finance.entry.CHARGEBACK_RECOVERY': 'Chargeback recovery',
  'finance.entry.MANUAL_ADJUSTMENT': 'Manual adjustment',
  'finance.entry.COMMISSION': 'Commission',
  'finance.entry.REFUND_CORRECTION': 'Refund correction',
  'finance.entry.REFUND_NETTING': 'Refund netting',
  'finance.entry.CHARGEBACK_CLAWBACK': 'Chargeback clawback',
  'finance.subtitle': 'The ledger for a period, and the daily reconciliation state.',
  'finance.ledger.title': 'Ledger',
  'finance.grossCharged': 'Gross charged',
  'finance.bakeryPayable': 'Bakery payable',
  'finance.ownerCommissionPayable': 'Owner commission payable',
  'finance.ownerCommissionVatSplit': '{net} excluding VAT + {vat} VAT',
  'finance.platformRevenue': 'Platform revenue',
  'finance.guestMoneyHeld': 'Guest money held',
  'finance.serviceFeeRevenue': 'Service fee',
  'finance.deliveryFeeRevenue': 'Delivery fee',
  'finance.deliveryCostActual': 'Delivery cost, actual',
  'finance.taxCollected': 'Tax collected',
  'finance.refunds': 'Refunds',
  'finance.chargebacks': 'Chargebacks',
  'finance.tipsForwarded': 'Tips forwarded to couriers',
  'finance.deliveryMargin': 'Delivery margin',
  'finance.stripeFees': 'Stripe fees',
  'finance.stripeFeesEstimatedCount': 'Estimated at 3.15% + EUR 0.25 for {count} of the orders',
  'finance.reconciliation.title': 'Reconciliation',
  'finance.reconciliation.subtitle':
    'Stripe balance transactions checked against the summed per-order allocation snapshots. Any drift raises one alert.',
  'finance.reconciliation.clean': 'Clean - no drift',
  'finance.reconciliation.drift': 'Drift of {amount}',
  'finance.reconciliation.expected': 'Expected',
  'finance.reconciliation.observed': 'Observed',
  'finance.reconciliation.expectedFees': 'Stripe fees, expected',
  'finance.reconciliation.observedFees': 'Stripe fees, observed',
  'finance.reconciliation.period': 'Period {from} to {to}',
  'finance.reconciliation.ranAt': 'Ran {time}',
  'finance.reconciliation.run': 'Run reconciliation now',
  'finance.reconciliation.discrepancies': 'Discrepancies',
  'finance.reconciliation.noDiscrepancies': 'No discrepancy recorded.',
  'finance.reconciliation.column.order': 'Order',
  'finance.reconciliation.column.stripeObject': 'Stripe object',
  'finance.reconciliation.column.expected': 'Expected',
  'finance.reconciliation.column.observed': 'Observed',
  'finance.reconciliation.column.note': 'Note',
  'bakeries.field.paymentFrequency': 'Approval rhythm',
  'bakeries.field.paymentDaily': 'Daily',
  'bakeries.field.paymentWeekly': 'Weekly',
  'bakeries.field.paymentFrequencyScheduled': 'Takes effect Monday {date}.',

  // -- settings (OPS-025) ---------------------------------------------------
  /* The two platform-wide ordering switches. The two descriptions below are
     the only prose on the panel: each says what a guest can do while the box
     is ticked, which is the one thing the label itself does not say. */
  'settings.title': 'Settings',
  'settings.tab.ordering': 'Ordering',
  'settings.tab.delivery': 'Delivery',
  'settings.tab.counters': 'Counters',
  'settings.tab.whatsapp': 'WhatsApp',
  'settings.tab.support': 'Support',
  'settings.ordering.title': 'Ordering',
  'settings.whatsapp.title': 'WhatsApp',
  'settings.whatsapp.on': 'On',
  'settings.whatsapp.off': 'Off',
  'settings.whatsapp.recipients': 'New-order alerts to',
  'settings.whatsapp.templates': 'WhatsApp templates',
  'settings.whatsapp.template': 'Template',
  'settings.whatsapp.language': 'Language',
  'settings.whatsapp.status': 'Status at Meta',
  'settings.whatsapp.submit': 'Submit to Meta',
  'settings.guestSupport.title': 'Guest support',
  'settings.guestSupport.whatsapp': 'WhatsApp number',
  'settings.guestSupport.phone': 'Phone number',
  'settings.guestSupport.invalid': 'International format, starting with + and the country code',
  'settings.ordering.preorder': 'Pre-order',
  'settings.ordering.preorder.desc': 'Guests schedule a delivery for a coming morning.',
  'settings.ordering.immediate': 'Immediate delivery',
  'settings.ordering.immediate.desc':
    'Guests order for today, delivered as soon as a bakery and a courier can.',
  /* The delivery adapter an order placed from now on is pinned to (RTE-023).
     The one line under the select is the whole answer to "what happens to the
     orders already out there", which is the question the control raises and
     does not answer. */
  'settings.delivery.title': 'Delivery provider',
  'settings.delivery.provider': 'Provider',
  'settings.delivery.next':
    'Applies to orders placed from now on; orders already placed keep their provider.',
  /* RTE-023: nothing chosen yet - no order can be placed until one is. */
  'settings.delivery.notSet': 'Not set',
  /* NTF-001 (founder decision 2026-09-24): the platform's guest texts. */
  'settings.delivery.guestSms': 'Guest SMS',
  /* RTE-027 / OPS-005: the courier quote above which checkout is refused. */
  'settings.courierCeiling.title': 'Courier quote ceiling',
  'settings.courierCeiling.ceiling': 'Maximum courier quote (EUR, VAT included)',
  'settings.courierCeiling.hint': 'Guests cannot check out when the courier quotes more.',
  'settings.courierCeiling.invalid': 'An amount above zero, at most 1000.00',
  'settings.save': 'Save',
  'settings.handheld.title': 'Counter alerts',
  'settings.handheld.batteryLowPercent': 'Battery low (%)',
  'settings.handheld.clockSkewRaiseMillis': 'Clock skew raises at (ms)',
  'settings.handheld.clockSkewResolveMillis': 'Clock skew clears under (ms)',
  'settings.handheld.storageLowMb': 'Storage low (MB)',
  'settings.handheld.storageResolveMb': 'Storage clears above (MB)',
  'settings.handheld.crashLoopCount': 'Crashes for a loop',
  'settings.handheld.crashLoopWindowMinutes': 'Crash window (minutes)',
  'settings.handheld.updateStalledHours': 'Update stalled after (hours)',

  // -- quality and ratings (OPS-007) ----------------------------------------
  'quality.title': 'Quality and ratings',
  'quality.subtitle': 'Internal quality assurance. Guest ratings are never published.',
  'quality.onTime': 'On time',
  'quality.preparation': 'Preparation',
  'quality.medianCourierWait': 'Median courier wait',
  'quality.failedDeliveries': 'Failed deliveries',
  'quality.missingItems': 'Missing items',
  'quality.minutes': '{value} min',
  'quality.minutesSeeded': '{value} min (default)',
  'quality.preparationHistory.chart': 'Preparation time per order',
  'quality.preparationHistory.average': 'Average',
  'quality.preparationHistory.p75': 'p75',
  'quality.preparationHistory.order': 'Order',
  'quality.preparationHistory.ready': 'Ready',
  'quality.preparationHistory.empty': 'No measured orders',
  'quality.byBakery': 'By bakery',
  'quality.column.bakery': 'Bakery',
  'quality.column.orders': 'Orders',
  'quality.column.onTime': 'On time',
  'quality.column.rejections': 'Rejections',
  'quality.column.rating': 'Product rating',
  'ratings.title': 'Ratings',
  'ratings.averageDelivery': 'Average delivery rating',
  'ratings.averageProducts': 'Average product rating',
  'ratings.none': 'No rating in this period.',
  'ratings.subject': 'Subject',
  'ratings.subject.DELIVERY': 'Delivery',
  'ratings.subject.PRODUCTS': 'Products',
  'ratings.maxStars': 'At most this many stars',
  'ratings.stars': { one: '{count} star', other: '{count} stars' },
  'ratings.noComment': 'No comment left.',

  // -- coverage map (OPS-004, ALC-009) --------------------------------------
  'map.title': 'Coverage',
  'map.subtitle':
    'Where each bakery actually wins, sampled from the allocator itself. Nothing here is editable: it follows the shops.',
  'map.mode': 'Storefront pool',
  'map.canvas': 'Map of bakery coverage, {mode}',
  'map.legend.title': 'Bakeries on this map',
  'map.legend.hint':
    'The solid areas are who wins, and neighboring areas never share a color. Click an area to name its bakery; switch a bakery off to remove it from the map.',
  'map.legend.bakery': 'Bakery',
  'map.legend.share': 'Share of served area',
  'map.legend.action': 'Show',
  'map.legend.toggle': 'Show {name}',
  'map.radius': 'Service radius: {value} km',
  'map.squareKm': '{value} km2',
  'map.popup.owner': 'Served by {name}',
  'map.thinCover': 'Outline areas only one bakery reaches',
  'map.thinCover.hint':
    'ALC-009: a second eligible bakery is what keeps an area running seven days. One closure ends service on this ground.',
  'map.thinCover.count': '{value} km2 is reached by a single bakery.',
  'map.gap.count': '{value} km2 of Paris ({share}%) is reached by no bakery.',
  'map.properties.count': '{count} apartments are drawn, one dot each.',
  'map.owners': 'Clients',
  'map.sampledAt': 'Sampled on a {value} m grid; every cell is a real allocation decision.',
  'map.noToken.title': 'The map cannot be drawn',
  'map.noToken.body': 'This build has no public map token. Set it and rebuild the portal:',
  'map.noToken.hint':
    'A publishable token, never the server geocoding secret. The coverage figures below are complete without it.',

  // -- audit (OPS-008) ------------------------------------------------------
  'audit.title': 'Audit log',
  'audit.subtitle':
    'Immutable. Pricing, stock, order state, address instructions, refunds and access changes.',
  'audit.subjectType': 'Subject type',
  'audit.subjectId': 'Subject id',
  'audit.column.at': 'When',
  'audit.column.actor': 'Actor',
  'audit.column.action': 'Action',
  'audit.column.subject': 'Subject',
  'audit.column.change': 'Change',
  'audit.before': 'Before',
  'audit.after': 'After',
  'audit.detail': 'Detail',
  'audit.noChange': 'No payload recorded.',
  'audit.empty': 'No audit entry matches those filters.',

  // -- scenario runs (OPS-038) ----------------------------------------------
  'runs.title': 'Scenario runs',
  'runs.subtitle':
    'What each guest-ordering run played, and what the guest, the counter and the courier saw.',
  'runs.empty': 'No run evidence has been published.',
  'runs.back': 'Scenario runs',
  'runs.column.run': 'Run',
  'runs.column.scenario': 'Scenario',
  'runs.column.started': 'Started',
  'runs.column.lane': 'Lane',
  'runs.column.verdict': 'Verdict',
  'runs.column.orders': 'Orders',
  'runs.column.disagreements': 'Disagreements',
  'runs.detail.title': 'Run',
  'runs.field.scenarioPath': 'Scenario file',
  'runs.field.comparisons': 'Comparisons',
  'runs.field.findings': 'Findings',
  'runs.field.findingsCount': '{open} open of {total}',
  'runs.field.screenshots': 'Screenshots',
  'runs.orders.title': 'Orders',
  'runs.orders.empty': 'This run placed no order.',
  'runs.orders.column.code': 'Order',
  'runs.orders.column.guest': 'Guest',
  'runs.orders.column.apartment': 'Apartment',
  'runs.orders.column.house': 'House',
  'runs.orders.column.placed': 'Placed',
  'runs.orders.column.outcome': 'Outcome',
  'runs.timeline.title': 'Timeline',
  'runs.timeline.empty': 'This run recorded no event.',
  'runs.timeline.column.platform': 'Platform time',
  'runs.timeline.column.wall': 'Wall time',
  'runs.timeline.column.guest': 'Guest',
  'runs.timeline.column.event': 'Event',
  'runs.shots.title': 'Screenshots',
  'runs.shots.empty': 'This run took no screenshot.',
  'runs.shots.observation': 'Observation',
  'runs.shots.previous': 'Previous',
  'runs.shots.next': 'Next',
  'runs.shots.position': '{at} of {total}',
  'runs.shots.failed': 'This screenshot could not be loaded.',
  'runs.shots.seat.guest': 'Guest',
  'runs.shots.seat.counter': 'Counter',
  'runs.shots.seat.courier': 'Courier',

  // The channel a notification to the guest went by (the Support tab).
  'channel.SMS': 'SMS',
  'channel.EMAIL': 'Email',
  'channel.WHATSAPP': 'WhatsApp',

  // -- API failures ---------------------------------------------------------
  'error.network': 'The API could not be reached.',
  'error.unexpected': 'Something went wrong.',
  'error.notFound': 'That does not exist, or it has moved.',
  'error.idempotency_conflict': 'That action was already submitted with different values.',
  'error.cancellation_closed': 'Cancellation is closed on this order; refund it instead.',
  'error.provider_limit':
    'The delivery provider refused that booking: the promised window no longer fits its limits. Move the promise first (delay pickup), then re-book.',
  'error.provider_update_ignored':
    'The delivery provider accepted that change and did not apply it: the booking is not where this order says it is. Open the job at the provider before promising anything else.',
  'error.not_dispatchable': 'This order cannot be dispatched from its current state.',
  'error.order_ended':
    'This order has ended: its delivery cannot be dispatched, re-booked or re-promised.',
  'error.capability_unavailable': 'The delivery provider does not support that capability.',
  'error.illegal_transition': 'That action is not allowed from the order’s current status.',
  'error.delivery_not_rebookable':
    'A live courier booking cannot be moved: cancel the provider job first, then re-book.',
  'error.provider_cancel_refused':
    'The delivery provider refused to cancel the job: nothing was re-booked.',
  'error.kill_city_required': 'Name the city to pause.',

  /* Only the wire keys whose wording an operator reads differently from the
     platform's. Every other messageKey in INTERNAL_ERROR_MESSAGE_KEYS resolves from
     the platform catalogue in `@croissants/i18n`, because `errorSentence` reads this
     catalogue first and that one after; a key whose sentence here would be
     byte-identical to the platform's does not belong in this file. The wire test in
     completeness.test.ts proves every key the mount can send renders, from one
     catalogue or the other (PRD NFR-006). */
  'error.validation': 'The request was not valid. Check the highlighted fields.',
  'error.idempotency_key_required': 'The request was missing its idempotency key. Try again.',
  'error.http': 'The request was refused.',
  'error.support_case_stale': 'The order changed. Refresh and decide again.',
  'error.refund_over_charged': 'That is more than the order has left to refund. Lower the amount.',
  'error.refund_order_not_final': 'The order has not ended yet. Decide this case once it has.',
  'error.internal': 'The platform hit an internal error. It has been logged.',
  'error.temporarily_unavailable': 'The platform did not answer in time. Try again in a moment.',
  'error.domain': 'That change is not allowed by the platform rules.',
  'error.rejection_reason_required': 'A rejection needs a reason.',
  'error.guest_choice_required':
    'The guest chose this shop themselves; it cannot be swapped silently.',
  'error.immediate_only': 'That action is only available in immediate mode.',
  'error.stale_event': 'A newer update already superseded that event.',
  'error.qr_invalid': 'That QR code does not resolve.',
  'error.session_invalid': 'The guest session is not valid.',
  'error.window_unavailable': 'That delivery window is no longer offerable.',
  /* Immediate delivery (OPS-025, GST-042): a window on today can go past while
     a guest is choosing it, and the platform can be selling nothing at all. */
  'error.delivery_unavailable': 'No delivery provider is set. Choose one in Settings.',
  'error.courier_quote_above_ceiling': 'The courier quote is above the ceiling.',
  // GST-010: the guest was placing several mornings and one of them stopped
  // being servable while they were on the review, so the order was refused.
  'error.stay_morning_unavailable': 'One morning of that order can no longer be served.',
  'error.no_bakery': 'No bakery can serve that address.',
  'error.choose_shop': 'The guest must choose a shop before this can proceed.',
  // Mode B2: the guest's session was bound to a shop that has since stopped
  // trading. An operator reads this on a guest's order, so it says which fact
  // moved rather than what the guest app does about it.
  'error.shop_changed': 'The shop serving that apartment is no longer available.',
  'error.minimum_basket': 'The basket is below the minimum.',
  'error.payment_declined': 'The payment was declined.',
  'error.tip_unsupported': 'The delivery provider does not support tips.',
  'error.tip_window_closed': 'The tip window has closed on that order.',
  'error.rating_before_delivery': 'That order has not been delivered, so it cannot be rated yet.',
  'error.order_not_closed': 'That order is not closed yet, so it has no invoices.',
  'error.qr_cap': 'That organization has reached its QR generation cap.',
  'error.no_qr_codes': 'This client has no apartment with a QR code yet.',
  'error.import_invalid': 'The import file was not valid.',
  'error.property_not_found': 'That property does not exist.',
  'error.partner_not_found': 'That partner organization does not exist.',
  'error.alert_not_found': 'That alert no longer exists.',
  'error.payout_not_found': 'That payout does not exist or is not awaiting review.',
  'error.storefront_mode_not_allowed':
    'That client is not sold this menu type. Tick it on the client page first.',
  'error.rate_amount_required': 'A payout minimum needs an amount.',
  'error.rate_kind': 'That rate kind is not supported.',
  'error.target_unpriced':
    'That house has agreed no price for something in this basket. Price it on their range, then move the order.',
  'error.target_closed': "That shop is closed on this order's day.",
  'error.refund_amount_unsupported':
    'A refund covers the whole order or whole lines. Select the lines that match the amount.',
  'error.order_line_unknown': 'One of the selected lines does not belong to this order.',
  'error.small_order_fee_incomplete':
    'The small-order fee and its threshold are set together, or both cleared.',
  'error.negative_commission':
    'That price is above the guest price, so the platform would be paying to sell it. Lower it, or raise the guest price first.',
  'error.guest_price_below_bakery_price':
    'That guest price is below what a baker keeps for this product. Lower their prices first.',
  'error.price_follows_parts': "This set's price follows its products, so it cannot be changed.",
  'error.set_parts_unpriced': 'Price every product of this set for this bakery first.',
  'error.bakery_price_required': 'Enter the bakery price.',
  'error.not_a_formule': 'Only a formule is made of other products.',
  'error.choice_not_in_pool':
    'That product is not on the platform menu, so it cannot be part of a formule.',
  'error.choice_retired': 'That product is retired and cannot be part of a formule.',
  'error.choice_in_published_formule':
    'A set on the menu still lists this product. Take the set off the menu, or take the product out of it, before retiring it.',
  'error.choice_needs_options':
    'A section the guest chooses from needs at least two options. Make it an included section, or add another option.',
  'error.formule_without_components':
    'A formule needs at least one line before it goes on the menu.',
  'error.product_code_taken':
    'That product code already names another product. Product codes come from the partner workbook and each names one product.',
  /* The counter fleet's release channel (OPS-029). */
  // -- the counter fleet (OPS-029, docs/plans/handheld-fleet.html 5) ---------
  'devices.title': 'Devices',
  'devices.search': 'Search devices',
  'devices.filter.status': 'Status',
  'devices.filter.version': 'Version',
  'devices.column.device': 'Device',
  'devices.column.shop': 'Shop',
  'devices.unpaired': 'Not paired',
  'devices.column.house': 'House',
  'devices.column.kind': 'Kind',
  'devices.column.deviceId': 'Device ID',
  'devices.column.version': 'Version',
  'devices.column.lastSeen': 'Last seen',
  'devices.column.status': 'Status',
  'devices.column.battery': 'Battery',
  'devices.column.network': 'Network',
  'devices.column.printer': 'Printer',
  'devices.column.config': 'Config',
  'devices.column.doze': 'Doze',
  'devices.version.unknown': 'Not reported',
  /** The second line of the Kind cell, under "Sunmi", for a counter AVD. */
  'devices.kind.emulator': 'emulator',
  'devices.network.WIFI': 'Wi-Fi',
  'devices.network.CELLULAR': 'Cellular',
  'devices.network.ETHERNET': 'Ethernet',
  'devices.network.NONE': 'None',
  'devices.printer.ok': 'OK',
  'devices.printer.preparing': 'Busy',
  'devices.printer.noPaper': 'No paper',
  'devices.printer.fault': 'Fault',
  'devices.printer.absent': 'Absent',
  'devices.doze.exempt': 'Exempt',
  'devices.doze.throttled': 'Throttled',
  'devices.charging': 'On charge',
  'devices.rowActions': 'Device actions',
  'devices.rowActionsFor': 'Actions for {name}',
  'device.identity.title': 'Device',
  'device.field.label': 'Label',
  'device.field.androidId': 'Android ID',
  'device.field.serial': 'Serial',
  'device.field.deviceOwner': 'Device owner',
  'device.owner.yes': 'Owner',
  'device.field.storage': 'Storage free',
  'device.field.uptime': 'Uptime',
  'device.field.skew': 'Clock skew',
  'device.field.staged': 'Staged build',
  'device.field.lastInstall': 'Last install',
  'device.field.configReported': 'Config reported',
  'device.field.configResolved': 'Config resolved',
  'device.uptime.value': '{hours}h {minutes}m',
  'device.install.INSTALLED': 'Installed',
  'device.install.REFUSED_BY_INSTALLER': 'Refused by the installer',
  'device.install.HASH_MISMATCH': 'Hash mismatch',
  'device.install.SIGNATURE_MISMATCH': 'Signature mismatch',
  'device.install.DOWNLOAD_FAILED': 'Download failed',
  'device.commands.title': 'Commands',
  'device.commands.column.command': 'Command',
  'device.commands.column.issued': 'Issued',
  'device.commands.column.state': 'State',
  'device.commands.column.by': 'By',
  'device.command.RING': 'Ring',
  'device.command.TEST_PRINT': 'Test print',
  'device.command.UPLOAD_DIAGNOSTICS': 'Upload diagnostics',
  'device.command.RESTART_APP': 'Restart app',
  'device.command.RESYNC': 'Resync',
  'device.command.SHOW_MESSAGE': 'Send message',
  'device.command.state.waiting': 'Waiting',
  'device.command.state.delivered': 'Delivered',
  'device.command.sent': '{command} sent',
  'device.outcome.DONE': 'Done',
  'device.outcome.FAILED': 'Failed',
  'device.outcome.UNSUPPORTED': 'Not supported',
  'device.outcome.EXPIRED': 'Expired',
  'device.rename': 'Rename',
  'device.retire': 'Retire',
  'device.retire.title': 'Retire {name}?',
  'device.retire.body': 'It stops receiving updates. Pairing it again brings it back.',
  'device.retired.done': 'Device retired',
  'device.message.title': 'Send a message',
  'device.message.text': 'Message',
  'device.message.count': '{n} / {max}',
  'device.message.send': 'Send',
  'device.timeline.title': 'Timeline',
  'device.timeline.column.at': 'When',
  'device.timeline.column.what': 'What',
  'device.timeline.column.who': 'Who',
  'device.timeline.column.detail': 'Detail',
  'device.timeline.empty': 'Nothing recorded about this device yet',
  'device.config.title': 'Configuration',
  'device.diagnostics.title': 'Diagnostics',
  'device.diagnostics.column.document': 'Document',
  'device.diagnostics.column.size': 'Size',
  'device.diagnostics.column.uploaded': 'Uploaded',
  'device.diagnostics.kind.CRASH': 'Crash report',
  'device.diagnostics.kind.BUNDLE': 'Diagnostic bundle',
  'device.diagnostics.download': 'Download',
  'device.diagnostics.failed': 'Could not download that document',
  'device.diagnostics.empty': 'This counter has left nothing behind.',
  'handheldConfig.title': 'Counter devices',
  'handheldConfig.alarmForcesVolume': 'Alarm forces the volume up',
  'handheldConfig.alarmMinimumVolumePercent': 'Minimum alarm volume (%)',
  'handheldConfig.heartbeatSeconds': 'Heartbeat (seconds)',
  'handheldConfig.stageOnCellular': 'Download updates on cellular',
  'handheldConfig.useDefault': 'Use default',
  'releases.title': 'Releases',
  // The accessible name of a tick or a dash in the Signed and Published columns (#2004).
  'releases.yes': 'Yes',
  'releases.no': 'No',
  'releases.state.title': 'What the fleet is offered',
  'releases.state.latest': 'Latest published',
  'releases.state.minimum': 'Minimum required',
  'releases.state.blocksAt': 'Blocks at',
  'releases.state.rollout': 'Rollout',
  'releases.state.none': 'None',
  'releases.state.canary': {
    one: '{count} canary shop (as published)',
    other: '{count} canary shops (as published)',
  },
  'releases.state.percent': '{percent}% of the rest of the fleet',
  'releases.canary.title': 'Canary shops for the next publish',
  'releases.canary.save': 'Save canary shops',
  'releases.canary.done': 'Canary shops saved',
  'releases.builds.title': 'Builds',
  'releases.column.versionCode': 'Code',
  'releases.column.versionName': 'Version',
  'releases.column.size': 'Size',
  'releases.column.sha256': 'SHA-256',
  'releases.column.uploaded': 'Uploaded',
  'releases.column.signed': 'Signed',
  'releases.column.published': 'Published',
  'releases.rowActions': 'Build actions',
  'releases.rowActionsFor': 'Actions for {version}',
  'releases.action.sign': 'Sign',
  'releases.action.publish': 'Publish',
  'releases.upload': 'Upload build',
  'releases.upload.title': 'Upload a build',
  'releases.upload.file': 'APK file',
  'releases.upload.choose': 'Choose file',
  'releases.upload.versionCode': 'Version code',
  'releases.upload.versionName': 'Version name',
  'releases.upload.confirm': 'Upload',
  'releases.upload.done': '{version} uploaded',
  'releases.sign.title': 'Sign {version}',
  'releases.sign.manifest': 'Manifest to sign',
  'releases.sign.copy': 'Copy manifest',
  'releases.sign.copied': 'Manifest copied',
  'releases.sign.copyFailed': 'Could not copy the manifest',
  'releases.sign.signature': 'Signature',
  'releases.sign.keyId': 'Key',
  'releases.sign.keyId.primary': 'Primary',
  'releases.sign.keyId.reserve': 'Reserve',
  'releases.sign.confirm': 'Record signature',
  'releases.sign.done': '{version} signed',
  'releases.publish.title': 'Publish {version}',
  'releases.publish.body':
    'It becomes the latest build and is offered to the canary shops only. Widening it to the rest of the fleet is a separate act.',
  'releases.publish.confirm': 'Publish',
  'releases.publish.done': '{version} published',
  'releases.force.title': 'Force update',
  'releases.force.version': 'Build',
  'releases.force.grace': 'Grace period (minutes)',
  'releases.force.sentence': 'Every handheld below {version} blocks at {when} until it is updated.',
  'releases.force.confirm': 'Force update',
  'releases.force.done': 'Force set',
  'releases.force.remove': 'Remove force',
  'releases.force.remove.done': 'Force removed',
  'releases.force.noPublished': 'Nothing is published yet, so there is nothing to force.',
  'releases.action.halt': 'Halt rollout',
  'releases.action.unhalt': 'Clear halt',
  'releases.widen.title': 'Widen the rollout',
  'releases.widen.percent': 'Share of the rest of the fleet (%)',
  'releases.widen.confirm': 'Widen',
  'releases.widen.done': 'Rollout widened to {percent}%',
  'releases.halt.title': 'Halt {version}',
  'releases.halt.body':
    'Counters that have not installed it stop being offered it and are offered the previous build instead. Counters already on it keep it.',
  'releases.halt.confirm': 'Halt rollout',
  'releases.halt.done': '{version} halted',
  'releases.unhalt.title': 'Clear the halt on {version}',
  'releases.unhalt.body':
    'It becomes publishable again. Publishing it restarts the rollout at the canary shops.',
  'releases.unhalt.confirm': 'Clear halt',
  'releases.unhalt.done': 'Halt cleared on {version}',
  'releases.adoption.title': 'Adoption',
  'releases.adoption.column.version': 'Build',
  'releases.adoption.column.devices': 'Counters',
  'releases.adoption.column.failed': 'Failed installs',
  'releases.adoption.unnumbered': 'Unnumbered',
  'releases.adoption.empty': 'No counter has reported a build yet.',

  'error.run_not_found': 'No run evidence has been published under that id.',
  'error.stripe_identity_locked':
    'Stripe has verified these details, so they can\u2019t be changed. The partner creates a new account to use new ones.',
  'order.exchange.open': 'Open {event} details',
  'order.exchange.emptyBody': 'No body',
  'order.exchange.noResponse': 'No response retained',
  'order.exchange.headers': 'Headers',
  'order.exchange.raw': 'Retained body text',
  'order.exchange.platformTime': 'Platform time',
  'order.exchange.answered': 'Answered {time}',
  'order.exchange.legacy':
    'Only the event payload was retained for this older event. HTTP headers and the response are unavailable.',
  'order.exchange.request': 'Request',
  'order.exchange.response': 'Response',
  'order.exchange.webhook': 'Received payload',
  'order.exchange.ack': 'Acknowledgment',
  'order.exchange.OUTBOUND': 'API call',
  'order.exchange.WEBHOOK': 'Webhook',
  'order.exchange.EVENT': 'Provider event',

  // -- support (issue #2272) -------------------------------------------------
  'support.cases.title': 'Cases',
  'support.decided.title': 'Decided',
  'support.payments.title': 'Payments',
  'support.col.order': 'Order',
  'support.col.case': 'Case',
  'support.col.reasons': 'Reasons',
  'support.col.house': 'Bakery',
  'support.col.out': 'Given back',
  'support.col.decidedBy': 'Decided by',
  'support.col.decided': 'Decided',
  'support.col.opened': 'Opened',
  'support.filter.OPEN': 'Open',
  'support.filter.DECIDED': 'Decided',
  'support.list.empty.OPEN': 'Nothing waiting.',
  'support.list.empty.DECIDED': 'No decided case.',
  'support.list.overdue': 'overdue',
  'support.pick': 'Pick a case.',
  'support.reason.REMOVED': 'Items removed',
  'support.reason.LOST': 'Not delivered',
  'support.reason.CANCELLED': 'Order canceled',
  'support.reason.COMPLAINT': 'Guest complaint',
  'support.reason.DISPUTE': 'Bank dispute',
  'support.reason.FRAUD': 'Fraud warning',
  'support.reason.STRIPE': 'Something in Stripe',
  'support.reason.OTHER': 'Other',
  'support.reason.WASTED_RIDER': 'Rider sent for nothing',
  'support.reason.REFUND_REFUSED': 'Refund refused',
  'support.case.refused': 'Stripe refused the refund: {reason}',
  'support.outside': 'Paid outside Stripe',
  'support.confirm.outside': 'Record {amount} paid outside Stripe',
  'support.decided.outside': 'Paid outside Stripe.',
  'support.case.code': 'Case {code}',
  'support.case.order': 'Order {code}',
  'support.case.opened': 'opened {at}',
  'support.case.dueBy': 'answer by {at}',
  'support.strip.cases': 'Cases on this order:',
  'support.strip.this': 'this case',
  'support.strip.paidTogether': 'Paid together:',
  'support.section.happened': 'What happened',
  'support.section.decision': 'Your decision',
  'support.section.outcome': 'What will happen',
  'support.edit.reason': 'Reason',
  'support.edit.otherText': 'Which reason',
  'support.edit.detail': 'Detail',
  'support.edit.save': 'Save',
  'support.guest.heading': 'Give the guest back',
  'support.guest.fixedHeading': 'Which lines the {amount} was for',
  'support.guest.MISSING': 'What they did not get',
  'support.guest.NOTHING': 'Nothing',
  'support.guest.EVERYTHING': 'Everything they paid',
  'support.guest.SPREAD': 'Spread over the whole order',
  'support.guest.CUSTOM': 'Custom',
  'support.guest.fixedRule': 'Everything left',
  'support.payer.heading': 'Who pays for it',
  'support.payer.headingBank': 'If the bank rules against us, who pays',
  'support.payer.BAKERY': 'The bakery',
  'support.payer.COURIER': 'The courier',
  'support.payer.CROISSANT': 'Croissant',
  'support.payer.CUSTOM': 'Custom split',
  'support.suggested': 'Suggested',
  'support.lock.unlock': 'Unlock any amount',
  'support.lock.adminOnly': 'Other amounts: admin only',
  'support.lock.unlockedBy': 'Unlocked by {name}',
  'support.lock.relock': 'Lock again',
  'support.table.line': 'Line',
  'support.table.guestPrice': 'Guest price',
  'support.table.bakerPrice': 'Baker price',
  'support.table.already': 'Already given back',
  'support.table.out.guest': 'Given back to the guest',
  'support.table.out.bank': 'Taken by the bank',
  'support.table.out.stripe': 'Refunded in Stripe',
  'support.table.out.locked': 'locked',
  'support.table.out.unlocked': 'unlocked',
  'support.table.out.bankSet': 'set by the bank',
  'support.table.out.fraudRule': 'fraud rule',
  'support.table.bakery': 'Bakery',
  'support.table.courier': 'Courier',
  'support.table.croissant': 'Croissant',
  'support.table.unassigned': 'Unassigned',
  'support.table.total': 'Total',
  'support.table.cell': '{line}, {column}',
  'support.rowWhy.REMOVED': 'Removed by the bakery',
  'support.rowWhy.NOT_SUPPLIED': 'Not supplied',
  'support.rowWhy.NOT_DELIVERED': 'Not delivered',
  'support.rowWhy.REPORTED': 'Reported by the guest',
  'support.row.bankFee': 'Charged by the bank if we lose',
  'support.row.rider': '{fee} paid to the rider; the bakery or Croissant pays it',
  'support.row.discount': 'Courier price {price}, discount {discount} paid by Croissant',
  'support.mark.report': 'Mark as reported by the guest',
  'support.mark.received': 'Mark as received',
  'support.mark.got': 'Received',
  'support.mark.missing': 'Not received',
  'support.mark.fee': 'Fee',
  'support.summary.party': 'Party',
  'support.summary.before': 'Before',
  'support.summary.adjustment': 'Adjustment',
  'support.summary.after': 'After',
  'support.summary.guest': 'Guest pays',
  'support.summary.bakery': 'Bakery receives',
  'support.summary.courier': 'Courier receives',
  'support.summary.croissant': 'Croissant keeps',
  'support.summary.split': '{guest} guest + {croissant} Croissant',
  'support.summary.commission': '{amount} commission',
  'support.summary.service': '+ {amount} service fee',
  'support.summary.delivery': '+ {amount} delivery fee',
  'support.summary.courierAbove': '− {amount} courier above the delivery fee',
  'support.note': 'Note',
  'support.later': 'Decide later',
  'support.confirm.refund': 'Refund {amount}',
  'support.confirm.close': 'Close without refund',
  'support.confirm.cancel': 'Cancel the order and refund {amount}',
  'support.confirm.bank': 'Save and wait for the bank',
  'support.confirm.stripe': 'Record who pays',
  'support.blocked.unassigned': '{amount} unassigned.',
  'support.blocked.fixed': 'The lines must add up to {amount}.',
  'support.blocked.bounds': 'A red cell is out of bounds.',
  'support.decided.refund': 'Refund {amount}.',
  'support.decided.none': 'No refund.',
  'support.decided.bank': 'If lost, the bank takes {amount}.',
  'support.decided.stripe': 'Stripe refunded {amount} by hand.',
  'support.decided.bakery': 'Bakery pays {amount}.',
  'support.decided.bakeryUnsupplied': 'Bakery is not paid {amount} for what it never supplied.',
  'support.decided.courier': 'Courier pays {amount}.',
  'support.decided.croissant': 'Croissant gives up {amount}.',
  'support.decided.by': 'by {name}, {at}',
  'support.decided.WON': 'The bank ruled for us.',
  'support.decided.LOST': 'The bank ruled against us.',
  'support.exec.decided': 'Decision saved',
  'support.exec.sent': 'Sent to Stripe',
  'support.exec.accepted': 'Stripe accepted',
  'support.exec.confirmed': 'Stripe confirmed',
  'support.exec.steps': '{count} of 4 steps',
  'support.pay.doneToday': 'Done today',
  'support.pay.retrying': 'Retrying',
  'support.pay.needsHuman': 'Needs a human',
  'support.pay.stripeDid': 'Stripe did it, not us',
  'support.pay.table': 'Instructions to Stripe',
  'support.pay.col.instruction': 'Instruction',
  'support.pay.col.what': 'What',
  'support.pay.col.amount': 'Amount',
  'support.pay.col.progress': 'Progress',
  'support.pay.col.state': 'State',
  'support.pay.col.action': 'Action',
  'support.pay.retry': 'Retry now',
  'support.pay.kind.REFUND': 'Refund to the guest',
  'support.pay.kind.TRANSFER': 'Transfer to {name}',
  'support.pay.kind.REVERSAL': 'Take back from {name}',
  'support.pay.kind.CANCEL_PAYMENT': 'Cancel the payment',
  'support.pay.party.GUEST': 'the guest',
  'support.pay.party.BAKERY': 'the bakery',
  'support.pay.party.PARTNER': 'the partner',
  'support.pay.nextAttempt': 'Attempt {count}, next at {at}',
  'support.pay.empty': 'No instruction yet.',
  'support.pay.unrequested': 'Stripe did something we did not ask for',
  'support.pay.unrequested.REFUND': 'Refund of {amount} on order {order}',
  'support.pay.unrequested.CAPTURE': 'Capture of {amount} on order {order}',
  'support.pay.openCase': 'Open case {code}',
} as const;

export type EnOpsCatalogue = typeof en;
